import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import MonIpm from '../components/ipm/MonIpm';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
// Suivi « prochain acteur » : objet `suivi` des demandes (vue salarié ou gestion selon la route).
import { SuiviDemande, LigneSuivi, suiviEnCours } from '../components/suivi/SuiviDemande';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const ROLES_RH = ['admin', 'drh', 'rh_principal', 'assistant_rh', 'directeur_general', 'directeur_adjoint'];
const ROLES_DG = ['directeur_general', 'directeur_adjoint'];

// ─── Design tokens ────────────────────────────────────────────────────────────
const C = {
  green: '#1B5E20', green2: '#144A18', greenSoft: '#E8F5E9',
  red: '#C62828', redSoft: '#FFEBEE',
  orange: '#E65100', orangeSoft: '#FFF3E0',
  blue: '#0D47A1', blueSoft: '#E3F2FD',
  indigo: '#3949AB', indigoSoft: '#E8EAF6',
  purple: '#7B1FA2', purpleSoft: '#F3E5F5',
  grayInk: '#424242', grayBg: '#EEEEEE',
  ink: '#1A1A1A', ink2: '#4A4A4A', ink3: '#7A7A7A', ink4: '#A6A6A6',
  line: '#E0E0E0', line2: '#EFEFEF', bg: '#f7f7f7',
};
const R = 12;
const SHADOW = '0 1px 2px rgba(15,15,15,.04)';
const FONT = "'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif";

// ─── Utilitaires ──────────────────────────────────────────────────────────────
function toArray(data) {
  return Array.isArray(data) ? data : (data?.rows || data?.data || []);
}
function fmt(n) {
  if (n === null || n === undefined || n === '') return '—';
  return Number(n).toLocaleString('fr-FR') + ' FCFA';
}
function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('fr-FR');
}
function initiales(prenom, nom) {
  return ((prenom?.[0] || '') + (nom?.[0] || '')).toUpperCase() || '?';
}
function ancLabel(mois) {
  if (!mois) return '—';
  const ans = Math.floor(mois / 12);
  const m = mois % 12;
  if (ans === 0) return `${m} mois`;
  if (m === 0) return `${ans} an${ans > 1 ? 's' : ''}`;
  return `${ans} an${ans > 1 ? 's' : ''}, ${m} mois`;
}

// ─── Statuts ──────────────────────────────────────────────────────────────────
const STATUTS = {
  soumis:          { label: 'En attente de décision',       bg: C.orangeSoft, color: C.orange, dot: C.orange },
  approuve_dg:     { label: 'Approuvé par la Direction',    bg: C.blueSoft,   color: C.blue,   dot: C.blue },
  traite_rh:       { label: 'En traitement RH',             bg: C.indigoSoft, color: C.indigo, dot: C.indigo },
  transmis_compta: { label: 'Préparation du virement',      bg: C.purpleSoft, color: C.purple, dot: C.purple },
  fonds_debloques: { label: 'Fonds débloqués',              bg: C.purpleSoft, color: C.purple, dot: C.purple },
  actif:           { label: 'En cours de remboursement',    bg: C.greenSoft,  color: C.green,  dot: C.green },
  solde:           { label: 'Soldée',                       bg: C.grayBg,     color: C.grayInk,dot: C.grayInk },
  refuse:          { label: 'Refusée',                      bg: C.redSoft,    color: C.red,    dot: C.red },
  annule:          { label: 'Annulée',                      bg: C.redSoft,    color: C.red,    dot: C.red },
};

// ─── Styles communs ───────────────────────────────────────────────────────────
const CARD = { background: '#fff', border: `0.5px solid ${C.line}`, borderRadius: R, padding: 18, boxShadow: SHADOW, marginBottom: 14 };
const CARD_TITLE = { fontSize: 14, fontWeight: 600, margin: '0 0 14px', display: 'flex', alignItems: 'center', gap: 8, color: C.ink };
const INPUT_ST = { width: '100%', padding: '10px 12px', border: `1px solid ${C.line}`, borderRadius: 8, fontSize: 13.5, fontFamily: FONT, boxSizing: 'border-box', background: '#fff', outline: 'none' };
const LABEL_ST = { fontSize: 12.5, fontWeight: 600, color: C.ink2 };
const BTN_P = { display: 'inline-flex', alignItems: 'center', gap: 8, background: C.green, color: '#fff', padding: '10px 16px', borderRadius: 10, border: `1px solid ${C.green}`, cursor: 'pointer', fontWeight: 600, fontSize: 13.5, fontFamily: FONT };
const BTN_G = { display: 'inline-flex', alignItems: 'center', gap: 8, background: 'transparent', color: C.ink2, padding: '8px 12px', borderRadius: 10, border: `1px solid ${C.line}`, cursor: 'pointer', fontWeight: 500, fontSize: 13, fontFamily: FONT };
const BTN_D = { display: 'inline-flex', alignItems: 'center', gap: 8, background: '#fff', color: C.red, padding: '10px 16px', borderRadius: 10, border: '1px solid #E6C7C7', cursor: 'pointer', fontWeight: 600, fontSize: 13.5, fontFamily: FONT };
const BTN_W = { display: 'inline-flex', alignItems: 'center', gap: 8, background: '#fff', color: C.orange, padding: '10px 16px', borderRadius: 10, border: '1px solid #F0C9A1', cursor: 'pointer', fontWeight: 600, fontSize: 13.5, fontFamily: FONT };
const BTN_SM = { display: 'inline-flex', alignItems: 'center', gap: 6, background: '#fff', color: C.ink, padding: '6px 10px', borderRadius: 8, border: `1px solid ${C.line}`, cursor: 'pointer', fontWeight: 500, fontSize: 12, fontFamily: FONT };

// ─── SVG icons ────────────────────────────────────────────────────────────────
const IcoCheck = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>;
const IcoX     = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12"/></svg>;
const IcoArrow = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 5l7 7-7 7"/></svg>;
const IcoBack  = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>;
const IcoWarn  = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0,marginTop:1}}><path d="M10.3 3.9L2 18a2 2 0 0 0 1.7 3h16.6A2 2 0 0 0 22 18L13.7 3.9a2 2 0 0 0-3.4 0z"/><path d="M12 9v4M12 17h.01"/></svg>;
const IcoInfo  = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0,marginTop:1}}><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg>;
const IcoDoc   = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/></svg>;
const IcoUp    = () => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><path d="M17 8l-5-5-5 5"/><path d="M12 3v12"/></svg>;

// ─── BadgeStatut ──────────────────────────────────────────────────────────────
function BadgeStatut({ statut }) {
  const s = STATUTS[statut] || { label: statut, bg: C.grayBg, color: C.grayInk, dot: C.grayInk };
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 9px', borderRadius: 999, background: s.bg, color: s.color, fontSize: 11.5, fontWeight: 600, whiteSpace: 'nowrap' }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: s.dot, flexShrink: 0 }} />
      {s.label}
    </span>
  );
}

// ─── Timeline5 ────────────────────────────────────────────────────────────────
const ETAPES_5 = ['Demande', 'Validation DG', 'Traitement RH', 'Fonds débloqués', 'Actif'];
const DONE_AT  = { soumis:0, approuve_dg:1, traite_rh:2, transmis_compta:3, fonds_debloques:3, actif:4, solde:4 };

function Timeline5({ statut }) {
  const doneIdx = DONE_AT[statut] ?? 0;
  const isRefus = statut === 'refuse' || statut === 'annule';
  return (
    <div style={{ display: 'flex', padding: '12px 0', marginBottom: 14, overflowX: 'auto' }}>
      {ETAPES_5.map((lab, i) => {
        const done    = !isRefus && i < doneIdx;
        const current = !isRefus && i === doneIdx;
        return (
          <div key={i} style={{ flex: 1, minWidth: 80, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', padding: '0 8px' }}>
            {i < ETAPES_5.length - 1 && (
              <div style={{ position: 'absolute', top: 14, left: 'calc(50% + 16px)', right: 'calc(-50% + 16px)', height: 2, background: done ? C.green : C.line, zIndex: 0 }} />
            )}
            <div style={{ width: 30, height: 30, borderRadius: '50%', background: done ? C.green : '#fff', border: `2px solid ${done ? C.green : current ? C.green : C.line}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: done ? '#fff' : current ? C.green : C.ink3, fontWeight: 700, fontSize: 12, marginBottom: 6, position: 'relative', zIndex: 1, flexShrink: 0 }}>
              {done ? <IcoCheck /> : (i + 1)}
            </div>
            <div style={{ fontSize: 11, fontWeight: 600, textAlign: 'center', color: (done || current) ? C.ink : C.ink3 }}>{lab}</div>
          </div>
        );
      })}
    </div>
  );
}

// ─── AvatarCircle ─────────────────────────────────────────────────────────────
function AvatarCircle({ prenom, nom, size = 42 }) {
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', background: C.greenSoft, color: C.green2, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: Math.round(size * 0.33), flexShrink: 0 }}>
      {initiales(prenom, nom)}
    </div>
  );
}

// ─── CheckRow ─────────────────────────────────────────────────────────────────
function CheckRow({ k, v, ok, noTag }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '11px 0', borderBottom: `1px solid ${C.line2}`, fontSize: 13.5 }}>
      <span style={{ color: C.ink2 }}>{k}</span>
      <span style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
        {v}
        {!noTag && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '2px 7px', borderRadius: 999, fontSize: 11, fontWeight: 600, background: ok ? C.greenSoft : C.redSoft, color: ok ? C.green : C.red }}>
            {ok ? <IcoCheck /> : <IcoX />}
            {ok ? 'OK' : 'Non'}
          </span>
        )}
      </span>
    </div>
  );
}

// ─── KVGrid ───────────────────────────────────────────────────────────────────
function KVGrid({ items, cols = 4 }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: '10px 20px', padding: '14px 0', borderTop: `1px solid ${C.line2}`, borderBottom: `1px solid ${C.line2}`, marginBottom: 14 }}>
      {items.map((item, i) => (
        <div key={i}>
          <div style={{ fontSize: 11, color: C.ink3, textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 3 }}>{item.k}</div>
          <div style={{ fontSize: 13.5, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{item.v || '—'}</div>
        </div>
      ))}
    </div>
  );
}

// ─── SolvBar ──────────────────────────────────────────────────────────────────
function SolvBar({ salaire, charge, mensExtra, seuil = 50, label = 'Solvabilité' }) {
  const s = parseFloat(salaire) || 0;
  const c = parseFloat(charge) || 0;
  const m = parseFloat(mensExtra) || 0;
  const total = c + m;
  const pct   = s > 0 ? Math.round((total / s) * 100) : 0;
  const warn  = pct > seuil;

  if (!s) {
    return (
      <div style={{ background: '#FAFAFA', border: `1px solid ${C.line}`, borderRadius: 10, padding: '12px 14px', marginBottom: 14 }}>
        <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.05em', color: C.ink3, fontWeight: 600, marginBottom: 8 }}>{label}</div>
        <div style={{ fontSize: 13, color: C.ink3, display: 'flex', gap: 6, alignItems: 'center' }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg>
          Salaire de base non renseigné — Contactez le service RH pour mise à jour.
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: warn ? '#FFF8EE' : '#FAFAFA', border: `1px solid ${warn ? '#F5D9B0' : C.line}`, borderRadius: 10, padding: '12px 14px', marginBottom: 14 }}>
      <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.05em', color: warn ? '#B44A00' : C.ink3, fontWeight: 600, marginBottom: 8 }}>{label}</div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '3px 0', fontVariantNumeric: 'tabular-nums' }}><span>Retenues actuelles</span><span>{fmt(c)}</span></div>
      {m > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '3px 0', fontVariantNumeric: 'tabular-nums' }}><span>+ Ce prêt</span><span>{fmt(m)}</span></div>}
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '6px 0 3px', borderTop: `1px dashed ${warn ? '#E5C997' : '#D5D5D5'}`, marginTop: 5, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}><span>Total</span><span>{fmt(total)}</span></div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '3px 0', fontVariantNumeric: 'tabular-nums' }}><span>Salaire de base</span><span>{fmt(s)}</span></div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '3px 0', fontWeight: 700, color: warn ? '#B44A00' : C.green }}><span>Part retenue</span><span>{pct}% / {seuil}% max</span></div>
      <div style={{ height: 6, background: warn ? '#F3E0BE' : C.line2, borderRadius: 99, marginTop: 8, overflow: 'hidden' }}>
        <span style={{ display: 'block', height: '100%', width: `${Math.min(100, pct)}%`, background: warn ? C.orange : C.green, borderRadius: 99 }} />
      </div>
      {warn && (
        <div style={{ fontSize: 12, marginTop: 8, display: 'flex', gap: 6, color: '#5A3200' }}>
          <IcoWarn /><span>Dépassement du seuil {seuil}% — validation DG requise.</span>
        </div>
      )}
    </div>
  );
}

// ─── SimSolvCard (vue employe droite) ─────────────────────────────────────────
function SimSolvCard({ montant, duree, elig }) {
  const m    = parseFloat(montant) || 0;
  const d    = parseInt(duree)     || 1;
  const mens = d > 0 ? Math.round(m / d) : 0;
  const s    = parseFloat(elig?.employe?.salaire_base) || 0;
  const c    = parseFloat(elig?.charge_actuelle)       || 0;
  const seuil = elig?.seuil_solvabilite_pct || 50;
  const total = c + mens;
  const pct   = s > 0 ? Math.round((total / s) * 100) : 0;
  const warn  = pct > seuil;

  if (!m || !d) return (
    <div style={{ background: '#F0F5F0', border: '1px solid #D9E6DA', borderRadius: R, padding: '24px 18px', color: C.ink3, fontSize: 13, textAlign: 'center' }}>
      Renseignez le montant et la duree pour voir la simulation.
    </div>
  );

  return (
    <div style={{ background: warn ? '#FFF8EE' : '#F0F5F0', border: `1px solid ${warn ? '#F5D9B0' : '#D9E6DA'}`, borderRadius: R, padding: '16px 18px', marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, marginBottom: 10, color: warn ? '#B44A00' : C.green2, fontSize: 13 }}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11l3 3 8-8"/><path d="M20 12v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h10"/></svg>
        Simulation de votre prêt
      </div>
      {[['Montant demandé', fmt(m)], ['Durée', `${d} mois`]].map(([k, v], i) => (
        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', fontSize: 13.5, fontVariantNumeric: 'tabular-nums' }}><span>{k}</span><span>{v}</span></div>
      ))}
      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', fontSize: 15, fontWeight: 700, color: warn ? '#5A3200' : C.green2, fontVariantNumeric: 'tabular-nums' }}><span>Mensualité estimée</span><span>{fmt(mens)}</span></div>
      <div style={{ borderTop: `1px dashed ${warn ? '#E5C997' : '#C8D8CA'}`, margin: '8px 0' }} />
      {s > 0 ? (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 13, fontVariantNumeric: 'tabular-nums' }}><span>Retenues actuelles</span><span>{fmt(c)}</span></div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 13, fontVariantNumeric: 'tabular-nums' }}><span>+ Nouvelle mensualité</span><span>{fmt(mens)}</span></div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 13.5, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}><span>Total retenues</span><span>{fmt(total)}</span></div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 13, fontVariantNumeric: 'tabular-nums' }}><span>Salaire de base</span><span>{fmt(s)}</span></div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 13.5, fontWeight: 700, color: warn ? '#B44A00' : C.green }}><span>Part retenue</span><span>{pct}%</span></div>
          <div style={{ height: 6, background: warn ? '#F3E0BE' : '#D9E6DA', borderRadius: 99, marginTop: 6, overflow: 'hidden' }}>
            <span style={{ display: 'block', height: '100%', width: `${Math.min(100,pct)}%`, background: warn ? C.orange : C.green, borderRadius: 99 }} />
          </div>
          {warn && (
            <div style={{ fontSize: 12, marginTop: 8, paddingTop: 8, borderTop: `1px dashed #E5C997`, display: 'flex', gap: 6, color: '#5A3200' }}>
              <IcoInfo /><span>Au-dela du seuil de {seuil}% du salaire : la validation du DG sera requise. Envisagez une duree plus longue pour baisser la mensualite.</span>
            </div>
          )}
        </>
      ) : (
        <div style={{ fontSize: 13, color: C.ink3, display: 'flex', gap: 6, alignItems: 'center', paddingTop: 4, borderTop: `1px dashed #C8D8CA` }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg>
          Salaire non renseigné — la comparaison de solvabilité n'est pas disponible.
        </div>
      )}
    </div>
  );
}

// ─── DocPretPreview ───────────────────────────────────────────────────────────
function DocPretPreview({ demande }) {
  if (!demande) return null;
  const montantAccorde = demande.montant_accorde || demande.montant_demande;
  const mensualite     = demande.mensualite_retenue || Math.round(montantAccorde / demande.duree_mois);
  const dateDebut      = demande.date_debut_remboursement ? fmtDate(demande.date_debut_remboursement) : '—';
  let dateFin = '—';
  if (demande.date_debut_remboursement && demande.duree_mois) {
    const df = new Date(demande.date_debut_remboursement);
    df.setMonth(df.getMonth() + parseInt(demande.duree_mois));
    dateFin = df.toLocaleDateString('fr-FR');
  }
  return (
    <div style={{ background: '#fff', border: `1px solid ${C.line}`, borderRadius: 10, padding: '28px 32px', fontFamily: "Georgia,'Times New Roman',serif", color: '#111', boxShadow: '0 2px 10px rgba(0,0,0,.04)' }}>
      <div style={{ textAlign: 'center', marginBottom: 18, paddingBottom: 16, borderBottom: '2px double #111' }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: 20, letterSpacing: '.05em', fontFamily: FONT, color: C.green2 }}>
          <div style={{ width: 28, height: 28, borderRadius: '50%', background: C.green, color: '#fff', display: 'grid', placeItems: 'center', fontWeight: 700, fontSize: 14, flexShrink: 0 }}>S</div>
          SOGAS
        </div>
        <div style={{ fontSize: 12, color: C.ink2, marginTop: 6, fontFamily: FONT, letterSpacing: '.03em' }}>Societe de Gestion des Abattoirs du Senegal</div>
      </div>
      <div style={{ fontSize: 12, textAlign: 'right', color: C.ink3, marginBottom: 14, fontFamily: FONT }}>Ref. {demande.numero_dossier} · {fmtDate(demande.created_at)}</div>
      <h2 style={{ fontFamily: "Georgia,'Times New Roman',serif", fontSize: 17, textAlign: 'center', margin: '18px 0 22px', letterSpacing: '.08em', fontWeight: 700 }}>BON DE PRÊT SOGAS</h2>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 24px', fontSize: 13.5, marginBottom: 18 }}>
        {[['Bénéficiaire', `${demande.prenom} ${demande.nom}`], ['Matricule', demande.matricule], ['Site', demande.site_nom || '—'], ['Motif', demande.motif]].map(([k, v]) => (
          <div key={k}><div style={{ color: C.ink3, fontSize: 12, fontFamily: FONT }}>{k}</div><div style={{ fontWeight: 600, borderBottom: '1px dotted #B5B5B5', paddingBottom: 3 }}>{v}</div></div>
        ))}
      </div>
      <div style={{ border: '1.5px solid #111', padding: '10px 14px', textAlign: 'center', margin: '16px 0', fontSize: 14 }}>
        Montant accordé<strong style={{ display: 'block', marginTop: 4, fontSize: 18 }}>{fmt(montantAccorde)}</strong>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 24px', fontSize: 13.5, marginBottom: 18 }}>
        {[['Durée', `${demande.duree_mois} mois`], ['Mensualité', fmt(mensualite)], ['Date de début', dateDebut], ['Fin prévue', dateFin]].map(([k, v]) => (
          <div key={k}><div style={{ color: C.ink3, fontSize: 12, fontFamily: FONT }}>{k}</div><div style={{ fontWeight: 600, borderBottom: '1px dotted #B5B5B5', paddingBottom: 3 }}>{v}</div></div>
        ))}
      </div>
      <div style={{ fontSize: 12, lineHeight: 1.6, color: C.ink2, margin: '18px 0', fontFamily: FONT }}>
        Le présent prêt est accordé <strong>sans intérêt</strong>. Son remboursement s'effectue par retenue mensuelle automatique sur le salaire du bénéficiaire, à compter de la date de début indiquée. En cas de départ de l'entreprise, le solde restant devient immédiatement exigible.
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 40, marginTop: 40, fontFamily: FONT }}>
        {['Le Directeur Général', 'Le Bénéficiaire'].map(lab => (
          <div key={lab} style={{ textAlign: 'center' }}>
            <div style={{ borderTop: '1px solid #333', marginBottom: 6, height: 40 }} />
            <div style={{ fontSize: 12, color: C.ink2, fontWeight: 600 }}>{lab}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── ReqCard (carte demande pour listes DG/RH/Compta) ────────────────────────
function ReqCard({ d, seuil = 50, onVoir, onAction, actions }) {
  const salaire     = parseFloat(d.salaire_base) || 0;
  const charge      = parseFloat(d.charge_actuelle) || 0;
  const montant     = parseFloat(d.montant_accorde || d.montant_demande) || 0;
  const duree       = parseInt(d.duree_mois) || 1;
  const mens        = d.mensualite_retenue ? parseFloat(d.mensualite_retenue) : Math.round(montant / duree);
  const total       = charge + mens;
  const pct         = salaire > 0 ? Math.round((total / salaire) * 100) : 0;
  const warn        = pct > seuil;
  const ancMois     = d.date_embauche ? Math.floor((Date.now() - new Date(d.date_embauche)) / (1000*60*60*24*30.44)) : 0;
  const ancTexte    = ancLabel(ancMois);

  return (
    <div style={{ background: '#fff', border: `0.5px solid ${C.line}`, borderRadius: R, padding: 18, boxShadow: SHADOW, marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 14, flexWrap: 'wrap', marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <AvatarCircle prenom={d.prenom} nom={d.nom} size={42} />
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.ink }}>{d.prenom} {d.nom}</div>
            <div style={{ fontSize: 12.5, color: C.ink3, marginTop: 2, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <span>Matricule {d.matricule}</span>
              {d.site_nom && <><span>·</span><span>{d.site_nom}</span></>}
              {ancMois > 0 && <><span>·</span><span>Anciennete {ancTexte} · {d.type_contrat}</span></>}
            </div>
          </div>
        </div>
        <BadgeStatut statut={d.statut} />
      </div>

      <KVGrid cols={4} items={[
        { k: 'Montant demandé',  v: fmt(d.montant_demande) },
        { k: 'Durée',            v: `${d.duree_mois} mois` },
        { k: 'Mensualité',       v: fmt(mens) },
        { k: d.statut === 'soumis' ? 'Motif' : 'Numéro dossier', v: d.statut === 'soumis' ? d.motif : d.numero_dossier },
      ]} />

      {salaire > 0 && (
        <div style={{ background: warn ? '#FFF8EE' : '#FAFAFA', border: `1px solid ${warn ? '#F5D9B0' : C.line}`, borderRadius: 10, padding: '10px 14px', marginBottom: 14 }}>
          <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.05em', color: warn ? '#B44A00' : C.ink3, fontWeight: 600, marginBottom: 6 }}>Solvabilité consolidée</div>
          {[['Retenues actuelles', fmt(charge)], ['+ Ce prêt', fmt(mens)], ['Total', fmt(total)], ['Salaire de base', fmt(salaire)]].map(([k, v], i) => (
            <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '2px 0', borderTop: i === 2 ? `1px dashed ${warn ? '#E5C997' : '#D5D5D5'}` : 'none', paddingTop: i === 2 ? 6 : 2, marginTop: i === 2 ? 4 : 0, fontWeight: i === 2 ? 700 : 400, fontVariantNumeric: 'tabular-nums' }}><span>{k}</span><span>{v}</span></div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '2px 0', fontWeight: 700, color: warn ? '#B44A00' : C.green }}><span>Part retenue</span><span>{pct}% {warn ? `· dépasse le seuil ${seuil}%` : `· sous le seuil`}</span></div>
          <div style={{ height: 6, background: warn ? '#F3E0BE' : C.line2, borderRadius: 99, marginTop: 6, overflow: 'hidden' }}>
            <span style={{ display: 'block', height: '100%', width: `${Math.min(100,pct)}%`, background: warn ? C.orange : C.green, borderRadius: 99 }} />
          </div>
        </div>
      )}

      {d.commentaire_dg && (
        <div style={{ fontSize: 12.5, color: C.ink3, marginBottom: 10 }}>Commentaire DG : "{d.commentaire_dg}"</div>
      )}
      {suiviEnCours(d.suivi) && <div style={{ marginBottom: 10 }}><LigneSuivi suivi={d.suivi} /></div>}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 12.5, color: C.ink3 }}>Soumis le {fmtDate(d.created_at)}</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {onVoir && <button style={BTN_G} onClick={() => onVoir(d.id)}>Voir le dossier</button>}
          {actions}
        </div>
      </div>
    </div>
  );
}

// ─── FormulaireDemande ────────────────────────────────────────────────────────
function FormulaireDemande({ employe, token, onSuccess }) {
  const [elig, setElig]   = useState(null);
  const [form, setForm]   = useState({ montant: '', duree: 24, motifCat: 'Logement', motifDesc: '' });
  const [loading, setLoading] = useState(false);
  const [err, setErr]     = useState('');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/api/prets-sogas/eligibilite/${employe.id}`, { headers })
      .then(r => setElig(r.data))
      .catch(() => {});
  }, [employe.id]);

  const plafond   = elig?.plafond_applicable || 5000000;
  const dureeMax  = elig?.duree_max_mois || 24;
  const dureeMin  = elig?.duree_min_mois || 3;
  const seuil     = elig?.seuil_solvabilite_pct || 50;

  const submit = async (e) => {
    e.preventDefault();
    const montantNum = parseFloat(form.montant.replace(/\s/g, '').replace(',', '.'));
    if (!montantNum || montantNum <= 0) { setErr('Montant invalide'); return; }
    if (montantNum > plafond) { setErr(`Montant superieur au plafond de ${plafond.toLocaleString('fr-FR')} FCFA`); return; }
    const motifFinal = form.motifDesc ? `${form.motifCat} — ${form.motifDesc}` : form.motifCat;
    setErr(''); setLoading(true);
    try {
      await axios.post(`${API}/api/prets-sogas/demandes`, {
        employe_id: employe.id,
        montant_demande: montantNum,
        duree_mois: form.duree,
        motif: motifFinal,
      }, { headers });
      onSuccess();
    } catch (ex) {
      if (ex.response?.status === 403) {
        setErr("Pour faire une demande de prêt, contactez le service RH qui pourra l'enregistrer pour vous.");
      } else {
        setErr(ex.response?.data?.message || 'Erreur lors de la soumission');
      }
    } finally { setLoading(false); }
  };

  const montantNum = parseFloat(form.montant.replace(/\s/g, '').replace(',', '.')) || 0;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.15fr) minmax(0,1fr)', gap: 16, alignItems: 'start' }}>
      {/* Colonne gauche */}
      <div>
        {elig && (
          <div style={CARD}>
            <div style={CARD_TITLE}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
              Mon eligibilite
            </div>
            <CheckRow k="Anciennete" v={ancLabel(elig.employe?.anciennete_mois)} ok={elig.anciennete_ok} />
            <CheckRow k="Type de contrat" v={elig.employe?.type_contrat || '—'} ok={elig.contrat_ok} />
            <CheckRow k="Pret SOGAS actif" v={elig.pas_de_pret_actif ? 'Aucun' : 'Actif'} ok={elig.pas_de_pret_actif} />
            <CheckRow k="Plafond applicable" v={fmt(plafond)} noTag />
            {elig.eligible === false && (
              <div style={{ background: C.redSoft, borderRadius: 8, padding: '10px 12px', marginTop: 10, fontSize: 13, color: C.red }}>
                <strong>Non eligible : </strong>{(elig.erreurs || []).join(', ')}
              </div>
            )}
            <SolvBar salaire={elig.employe?.salaire_base} charge={elig.charge_actuelle} seuil={seuil} label="Solvabilité actuelle" />
          </div>
        )}

        {elig?.eligible !== false && (
          <form onSubmit={submit}>
            <div style={CARD}>
              <div style={CARD_TITLE}>
                <IcoDoc />
                Ma demande
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={LABEL_ST}>Montant souhaite (FCFA)</label>
                <input
                  type="text"
                  placeholder={`Max ${plafond.toLocaleString('fr-FR')}`}
                  value={form.montant}
                  onChange={e => setForm(f => ({ ...f, montant: e.target.value }))}
                  style={{ ...INPUT_ST, marginTop: 6 }}
                />
                <div style={{ fontSize: 12, color: C.ink3, marginTop: 4 }}>Plafond {fmt(plafond)}</div>
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={LABEL_ST}>Durée souhaitée</label>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: 6, marginBottom: 6 }}>
                  <strong style={{ fontSize: 18, fontVariantNumeric: 'tabular-nums' }}>{form.duree} mois</strong>
                  <span style={{ fontSize: 12, color: C.ink3 }}>{dureeMin} mois — {dureeMax} mois</span>
                </div>
                <input
                  type="range"
                  min={dureeMin}
                  max={dureeMax}
                  value={form.duree}
                  onChange={e => setForm(f => ({ ...f, duree: parseInt(e.target.value) }))}
                  style={{ WebkitAppearance: 'none', appearance: 'none', width: '100%', height: 6, background: C.line2, borderRadius: 99, outline: 'none', padding: 0, accentColor: C.green }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                <div>
                  <label style={LABEL_ST}>Motif</label>
                  <select value={form.motifCat} onChange={e => setForm(f => ({ ...f, motifCat: e.target.value }))}
                    style={{ ...INPUT_ST, marginTop: 6 }}>
                    {['Logement','Medical','Scolarite','Mariage','Autre'].map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={LABEL_ST}>Date souhaitee de deblocage</label>
                  <input type="date" style={{ ...INPUT_ST, marginTop: 6 }} />
                </div>
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={LABEL_ST}>Description du motif</label>
                <textarea
                  rows={3}
                  placeholder="Precisez le contexte de votre demande..."
                  value={form.motifDesc}
                  onChange={e => setForm(f => ({ ...f, motifDesc: e.target.value }))}
                  style={{ ...INPUT_ST, marginTop: 6, resize: 'vertical', minHeight: 80 }}
                />
              </div>

              {err && <div style={{ color: C.red, fontSize: 13, background: C.redSoft, padding: '8px 12px', borderRadius: 8, marginBottom: 12 }}>{err}</div>}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 10, borderTop: `1px solid ${C.line2}`, gap: 10, flexWrap: 'wrap' }}>
                <div style={{ fontSize: 12, color: C.ink3, flex: 1, minWidth: 200 }}>Votre demande sera examinee par la Direction Generale sous 5 jours ouvres.</div>
                <button type="submit" disabled={loading || !form.montant} style={BTN_P}>
                  {loading ? 'Envoi...' : 'Soumettre ma demande'}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>

      {/* Colonne droite */}
      <div>
        <SimSolvCard montant={montantNum} duree={form.duree} elig={elig} />
      </div>
    </div>
  );
}

// ─── TableauAmortissement ────────────────────────────────────────────────────
function TableauAmortissement({ montant, mensualite, dateDebut, duree }) {
  const m = parseFloat(montant) || 0;
  const men = parseFloat(mensualite) || 0;
  const nb = parseInt(duree) || 0;
  if (!m || !men || !nb || !dateDebut) return null;

  const lignes = [];
  let solde = m;
  for (let i = 1; i <= nb; i++) {
    const d = new Date(dateDebut);
    d.setMonth(d.getMonth() + (i - 1));
    const echeance = d.toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' });
    solde = Math.max(0, Math.round(m - men * i));
    lignes.push({ num: i, echeance, mensualite: men, solde });
  }

  const TH = { fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.05em', color: C.ink3, padding: '8px 10px', borderBottom: `1px solid ${C.line}`, whiteSpace: 'nowrap', textAlign: 'right' };
  const TD = (extra = {}) => ({ padding: '9px 10px', fontSize: 12, fontVariantNumeric: 'tabular-nums', borderBottom: `1px solid ${C.line2}`, textAlign: 'right', ...extra });

  return (
    <div style={{ ...CARD, padding: 0, overflow: 'hidden' }}>
      <div style={{ ...CARD_TITLE, padding: '14px 16px', borderBottom: `1px solid ${C.line2}` }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>
        Calendrier de remboursement
        <span style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 500, color: C.ink3 }}>{nb} echeances · {new Intl.NumberFormat('fr-FR').format(men)} FCFA/mois</span>
      </div>
      <div className="sogas-amort-wrap" style={{ maxHeight: 340, overflowY: 'auto' }}>
        <table className="sogas-amort-table" style={{ borderCollapse: 'collapse' }}>
          <thead style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
            <tr>
              <th style={{ ...TH, textAlign: 'left' }}>N°</th>
              <th style={{ ...TH, textAlign: 'left' }}>Échéance</th>
              <th style={TH}>Mensualité</th>
              <th style={TH}>Solde restant</th>
            </tr>
          </thead>
          <tbody>
            {lignes.map(l => (
              <tr key={l.num} style={{ background: l.solde === 0 ? C.greenSoft : 'transparent' }}>
                <td style={{ ...TD(), textAlign: 'left', color: C.ink3, fontWeight: 600 }}>{l.num}</td>
                <td style={{ ...TD(), textAlign: 'left', color: C.ink2 }}>{l.echeance}</td>
                <td style={{ ...TD(), color: C.ink, fontWeight: 500 }}>{new Intl.NumberFormat('fr-FR').format(l.mensualite)}</td>
                <td style={{ ...TD(), color: l.solde === 0 ? C.green : C.ink, fontWeight: l.solde === 0 ? 700 : 400 }}>
                  {new Intl.NumberFormat('fr-FR').format(l.solde)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="sogas-amort-cards">
          {lignes.map(l => (
            <div key={l.num} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 2px', borderBottom: `1px solid ${C.line2}`, background: l.solde === 0 ? C.greenSoft : 'transparent' }}>
              <span style={{ color: C.ink3, fontWeight: 600, fontSize: 11, minWidth: 24 }}>{l.num}</span>
              <span style={{ flex: 1, fontSize: 12, color: C.ink2 }}>{l.echeance}</span>
              <span style={{ fontVariantNumeric: 'tabular-nums', fontSize: 12, fontWeight: 500 }}>{new Intl.NumberFormat('fr-FR').format(l.mensualite)}</span>
              <span style={{ fontVariantNumeric: 'tabular-nums', fontSize: 12, color: l.solde === 0 ? C.green : C.ink3, fontWeight: l.solde === 0 ? 700 : 400, minWidth: 68, textAlign: 'right' }}>{new Intl.NumberFormat('fr-FR').format(l.solde)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── VueEmploye (Session 57 — 4 onglets mobiles) ────────────────────────────
const STATUT_LABEL = {
  soumis:          'En cours de traitement',
  approuve_dg:     'Approuvé',
  traite_rh:       'En cours de traitement',
  transmis_compta: 'En cours de paiement',
  actif:           'Actif — en remboursement',
  solde:           'Terminé',
  refuse:          'Refusé',
  en_cours:        'En cours de traitement',
  annule:          'Annulé',
  rembourse:       'Terminé',
};

const STATUT_COULEUR = {
  soumis:          { bg: '#FFF3E0', color: '#E65100' },
  approuve_dg:     { bg: '#E8F5E9', color: '#1B5E20' },
  traite_rh:       { bg: '#FFF3E0', color: '#E65100' },
  transmis_compta: { bg: '#E3F2FD', color: '#0D47A1' },
  actif:           { bg: '#E8F5E9', color: '#1B5E20' },
  solde:           { bg: '#F5F5F5', color: '#616161' },
  refuse:          { bg: '#FFEBEE', color: '#B71C1C' },
  en_cours:        { bg: '#FFF3E0', color: '#E65100' },
  annule:          { bg: '#FFEBEE', color: '#B71C1C' },
  rembourse:       { bg: '#F5F5F5', color: '#616161' },
};

function VueEmploye({ employe, token }) {
  const [onglet, setOnglet]     = useState('prets');
  const [demandes, setDemandes] = useState([]);
  const [selected, setSelected] = useState(null);
  const [succes, setSucces]     = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [dettes, setDettes]     = useState([]);
  const headers = { Authorization: `Bearer ${token}` };

  const charger = useCallback(() => {
    axios.get(`${API}/api/prets-sogas/mes-demandes`, { headers })
      .then(r => setDemandes(toArray(r.data.demandes || r.data)))
      .catch(() => {});
  }, [token]);

  useEffect(() => { charger(); }, [charger]);

  /* TODO_REACTIVER — useEffect GET /api/dettes/employe/mes-dettes
  useEffect(() => {
    axios.get(`${API}/api/dettes/employe/mes-dettes`, { headers })
      .then(r => setDettes(Array.isArray(r.data.dettes) ? r.data.dettes : []))
      .catch(() => {});
  }, [token]);
  TODO_REACTIVER */

  if (selected) return <DetailDemande id={selected} token={token} onBack={() => { setSelected(null); charger(); }} />;

  const onSuccess = () => {
    setSucces(true);
    setShowForm(false);
    charger();
    setTimeout(() => setSucces(false), 6000);
  };

  const STATUTS_PRETS_ACTIFS = new Set([
    'actif', 'active', 'en_remboursement', 'validee', 'approuve',
    'transmis_compta', 'approuve_dg', 'traite_rh',
  ]);
  const pretsActifs = demandes.filter(d => STATUTS_PRETS_ACTIFS.has(d.statut));
  const pretsTriees = [...demandes].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  // Demandes encore dans le circuit sans être un prêt en cours (ex. soumise) : affichées avec leur suivi.
  const demandesEnCours = pretsTriees.filter(d => !STATUTS_PRETS_ACTIFS.has(d.statut) && suiviEnCours(d.suivi));

  const TYPES_IPM = new Set(['ipm_facture', 'ipm_hospitalisation']);
  const dettesHorsIPM = dettes.filter(d => !TYPES_IPM.has(d.type_dette_code));
  const detteByRef = dettesHorsIPM.reduce((acc, d) => { acc[d.reference] = d; return acc; }, {});
  const retentionsActives = dettesHorsIPM.filter(d => ['validee', 'active'].includes(d.statut));
  const totalRetenuesMois = retentionsActives.reduce((s, d) => s + parseFloat(d.mensualite || 0), 0);
  const dettesTerminees = dettesHorsIPM.filter(d => d.statut === 'soldee');

  const fmtF = (n) => {
    if (n === null || n === undefined || n === '') return '—';
    return Number(n).toLocaleString('fr-FR') + ' F';
  };

  const fmtFinPret = (debut, duree) => {
    if (!debut || !duree) return '—';
    const d = new Date(debut);
    d.setMonth(d.getMonth() + parseInt(duree));
    return d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  };

  const calcRestant = (d) => {
    const montant = parseFloat(d.montant_accorde || d.montant_demande) || 0;
    const mens = parseFloat(d.mensualite_retenue) || Math.round(montant / (parseInt(d.duree_mois) || 1));
    if (!d.date_debut_remboursement || !mens) return montant;
    const moisEcoules = Math.floor((Date.now() - new Date(d.date_debut_remboursement)) / (1000 * 60 * 60 * 24 * 30.44));
    return Math.max(0, montant - mens * Math.max(0, moisEcoules));
  };

  const TABS = [
    { id: 'prets',      label: 'Mes prêts' },
    { id: 'demandes',   label: 'Demandes'  },
    { id: 'ipm',        label: 'IPM'  },
    { id: 'historique', label: 'Historique'},
  ];

  return (
    <div style={{ maxWidth: 480, margin: '0 auto', padding: '0 16px 24px', fontFamily: FONT }}>

      {/* Header */}
      <div style={{ padding: '20px 0 16px' }}>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#1A1A1A' }}>Mes finances</h2>
        <p style={{ margin: '4px 0 0', color: '#7A7A7A', fontSize: 13.5 }}>Prêts et retenues salariales</p>
      </div>

      {/* Bannière succès */}
      {succes && (
        <div style={{ background: '#E8F5E9', border: '1px solid #A5D6A7', borderRadius: 10, padding: '10px 14px', marginBottom: 14, fontSize: 13, color: '#1B5E20', display: 'flex', gap: 8, alignItems: 'center' }}>
          <IcoCheck />
          Demande soumise avec succès. Elle sera examinée sous 5 jours ouvrés.
        </div>
      )}

      {/* TODO_REACTIVER: Bandeau retenues ce mois */}
      {false /* TODO_REACTIVER */ && totalRetenuesMois > 0 && (
        <div style={{ background: '#E8F5E9', border: '1px solid #A5D6A7', borderRadius: 10, padding: '12px 14px', marginBottom: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: retentionsActives.length > 1 ? 8 : 0 }}>
            <span style={{ fontSize: 13, color: '#1B5E20', fontWeight: 600 }}>Retenues ce mois</span>
            <span style={{ fontSize: 15, fontWeight: 700, color: '#1B5E20', fontVariantNumeric: 'tabular-nums' }}>{fmtF(totalRetenuesMois)}</span>
          </div>
          {retentionsActives.length > 1 && retentionsActives.map(d => (
            <div key={d.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#388E3C', paddingTop: 4 }}>
              <span>{d.type_dette_libelle || d.reference}</span>
              <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtF(d.mensualite)}/mois</span>
            </div>
          ))}
        </div>
      )}

      {/* Navigation onglets — Prêts SOGAS + IPM */}
      <div style={{ display: 'flex', background: '#fff', border: '0.5px solid #E0E0E0', borderRadius: 10, padding: 3, marginBottom: 16, gap: 2 }}>
        {[{ id: 'prets', label: 'Mes prêts' }, { id: 'ipm', label: 'IPM' }].map(t => (
          <button key={t.id} onClick={() => setOnglet(t.id)} style={{
            flex: 1, border: 0, background: 'transparent', padding: '8px 4px',
            fontSize: 12.5, fontWeight: onglet === t.id ? 600 : 400,
            color: onglet === t.id ? '#1B5E20' : '#7A7A7A',
            cursor: 'pointer', fontFamily: FONT, borderRadius: 7,
            borderBottom: `2px solid ${onglet === t.id ? '#1B5E20' : 'transparent'}`,
          }}>{t.label}</button>
        ))}
      </div>

      {/* ─── Onglet 1 : Prêts SOGAS actifs ─── */}
      {onglet === 'prets' && <div>
        {demandesEnCours.map(d => (
          <div key={d.id} style={{ background: '#fff', border: '0.5px solid #E0E0E0', borderRadius: 12, padding: '14px 16px', marginBottom: 10, boxShadow: '0 1px 2px rgba(15,15,15,.04)' }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#1A1A1A' }}>Demande de prêt SOGAS — {fmtF(d.montant_demande)}</div>
            <div style={{ fontSize: 12, color: '#7A7A7A', marginTop: 2 }}>Demandée le {fmtDate(d.created_at)}</div>
            <SuiviDemande suivi={d.suivi} />
          </div>
        ))}
        {pretsActifs.length === 0 ? (
          <div style={{ background: '#fff', border: '0.5px solid #E0E0E0', borderRadius: 12, padding: '32px 20px', textAlign: 'center' }}>
            <div style={{ color: '#7A7A7A', fontSize: 14, marginBottom: 16 }}>Vous n'avez aucun prêt SOGAS en cours.</div>
            {!showForm ? (
              <button onClick={() => setShowForm(true)} style={{ ...BTN_P, justifyContent: 'center' }}>Faire une demande</button>
            ) : (
              <div style={{ marginTop: 8, textAlign: 'left' }}>
                <button onClick={() => setShowForm(false)} style={{ ...BTN_G, marginBottom: 12 }}><IcoBack /> Annuler</button>
                <FormulaireDemande employe={employe} token={token} onSuccess={onSuccess} />
              </div>
            )}
          </div>
        ) : pretsActifs.map(d => {
            const dette = detteByRef[d.numero_dossier];
            const montant = parseFloat(d.montant_accorde || d.montant_demande) || 0;
            const mens = dette ? parseFloat(dette.mensualite) : (parseFloat(d.mensualite_retenue) || Math.round(montant / (parseInt(d.duree_mois) || 1)));
            const restant = dette ? parseFloat(dette.montant_restant) : calcRestant(d);
            const pct = montant > 0 ? Math.min(100, Math.round(((montant - restant) / montant) * 100)) : 0;
            const couleur = STATUT_COULEUR[d.statut] || { bg: '#F5F5F5', color: '#616161' };
            return (
              <div key={d.id} style={{ background: '#fff', border: '0.5px solid #E0E0E0', borderRadius: 12, padding: '14px 16px', marginBottom: 10, boxShadow: '0 1px 2px rgba(15,15,15,.04)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#1A1A1A' }}>Prêt SOGAS</div>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 9px', borderRadius: 20, fontSize: 11.5, fontWeight: 600, background: couleur.bg, color: couleur.color, whiteSpace: 'nowrap' }}>
                    <span style={{ width: 5, height: 5, borderRadius: '50%', background: couleur.color, flexShrink: 0 }} />
                    {STATUT_LABEL[d.statut] || d.statut}
                  </span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px', marginBottom: 12 }}>
                  <div>
                    <div style={{ fontSize: 11, color: '#7A7A7A', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 2 }}>Reçu</div>
                    <div style={{ fontSize: 15, fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: '#1A1A1A' }}>{fmtF(montant)}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: '#7A7A7A', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 2 }}>Restant</div>
                    <div style={{ fontSize: 15, fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: restant > 0 ? '#1A1A1A' : '#1B5E20' }}>{fmtF(restant)}</div>
                  </div>
                </div>
                <div style={{ marginBottom: 10 }}>
                  <div style={{ height: 6, background: '#EFEFEF', borderRadius: 20, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${pct}%`, background: '#1B5E20', borderRadius: 20 }} />
                  </div>
                  <div style={{ fontSize: 11, color: '#7A7A7A', marginTop: 4, textAlign: 'right' }}>{pct}% remboursé</div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, color: '#4A4A4A' }}>
                  <span>Retenue : <strong style={{ color: '#1B5E20', fontVariantNumeric: 'tabular-nums' }}>{fmtF(mens)}/mois</strong></span>
                  <span>Fin : <strong>{fmtFinPret(d.date_debut_remboursement, d.duree_mois)}</strong></span>
                </div>
                {suiviEnCours(d.suivi) && <SuiviDemande suivi={d.suivi} />}
              </div>
            );
          })}
        </div>}

      {/* TODO_REACTIVER: Onglet 2 : Demandes (sections En attente / Terminées / Refusées + FormulaireDemande) */}
      {false /* TODO_REACTIVER */ && (() => {
        const STATUTS_EN_ATTENTE = new Set(['soumis','approuve_dg','traite_rh','transmis_compta','actif','active','en_remboursement','validee','approuve']);
        const STATUTS_TERMINEES  = new Set(['solde','rembourse']);
        const STATUTS_REFUSEES   = new Set(['refuse','annule']);
        const secEnAttente = pretsTriees.filter(d => STATUTS_EN_ATTENTE.has(d.statut));
        const secTerminees  = pretsTriees.filter(d => STATUTS_TERMINEES.has(d.statut));
        const secRefusees   = pretsTriees.filter(d => STATUTS_REFUSEES.has(d.statut));
        const renderCard = (d) => {
          const couleur = STATUT_COULEUR[d.statut] || { bg: '#F5F5F5', color: '#616161' };
          return (
            <div key={d.id} onClick={() => setSelected(d.id)} style={{ background: '#fff', border: '0.5px solid #E0E0E0', borderRadius: 12, padding: '14px 16px', marginBottom: 10, boxShadow: '0 1px 2px rgba(15,15,15,.04)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 500, color: '#1A1A1A', marginBottom: 3 }}>Prêt SOGAS — {fmtF(d.montant_demande)}</div>
                <div style={{ fontSize: 12, color: '#7A7A7A' }}>{fmtDate(d.created_at)}</div>
              </div>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 9px', borderRadius: 20, fontSize: 11.5, fontWeight: 600, background: couleur.bg, color: couleur.color, whiteSpace: 'nowrap', flexShrink: 0 }}>
                <span style={{ width: 5, height: 5, borderRadius: '50%', background: couleur.color, flexShrink: 0 }} />
                {STATUT_LABEL[d.statut] || d.statut}
              </span>
            </div>
          );
        };
        return (
          <div>
            {!showForm && (
              <button onClick={() => setShowForm(true)} style={{ ...BTN_P, width: '100%', justifyContent: 'center', marginBottom: 14 }}>
                Faire une demande de prêt
              </button>
            )}
            {showForm && (
              <div style={{ marginBottom: 14 }}>
                <button onClick={() => setShowForm(false)} style={{ ...BTN_G, marginBottom: 12 }}><IcoBack /> Annuler</button>
                <FormulaireDemande employe={employe} token={token} onSuccess={onSuccess} />
              </div>
            )}
            {pretsTriees.length === 0 ? (
              <div style={{ background: '#fff', border: '0.5px solid #E0E0E0', borderRadius: 12, padding: '32px 16px', textAlign: 'center', color: '#7A7A7A', fontSize: 14 }}>
                Aucune demande pour le moment.
              </div>
            ) : (
              <>
                {secEnAttente.length > 0 && (
                  <div style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 11.5, fontWeight: 600, color: '#7A7A7A', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8 }}>En attente</div>
                    {secEnAttente.map(renderCard)}
                  </div>
                )}
                {secTerminees.length > 0 && (
                  <div style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 11.5, fontWeight: 600, color: '#7A7A7A', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8 }}>Terminées</div>
                    {secTerminees.map(renderCard)}
                  </div>
                )}
                {secRefusees.length > 0 && (
                  <div style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 11.5, fontWeight: 600, color: '#7A7A7A', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8 }}>Refusées</div>
                    {secRefusees.map(renderCard)}
                  </div>
                )}
              </>
            )}
          </div>
        );
      })()}

      {/* Onglet IPM : relevé commun « Mon IPM » (socle du 3 octobre 2026 : dette = restes à charge) */}
      {onglet === 'ipm' && <MonIpm token={token} />}

      {/* TODO_REACTIVER: Onglet 4 : Historique (dettes soldee hors IPM) */}
      {false /* TODO_REACTIVER */ && (
        <div>
          {dettesTerminees.length === 0 ? (
            <div style={{ background: '#fff', border: '0.5px solid #E0E0E0', borderRadius: 12, padding: '32px 16px', textAlign: 'center', color: '#7A7A7A', fontSize: 14 }}>
              Aucun élément terminé.
            </div>
          ) : dettesTerminees.map(d => (
            <div key={d.id} style={{ background: '#fff', border: '0.5px solid #E0E0E0', borderRadius: 12, padding: '14px 16px', marginBottom: 10, boxShadow: '0 1px 2px rgba(15,15,15,.04)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#1A1A1A' }}>{d.type_dette_libelle}</div>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 9px', borderRadius: 20, fontSize: 11.5, fontWeight: 600, background: '#F5F5F5', color: '#616161', whiteSpace: 'nowrap' }}>
                  <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#616161', flexShrink: 0 }} />
                  Soldé
                </span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px' }}>
                <div>
                  <div style={{ fontSize: 11, color: '#7A7A7A', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 2 }}>Montant initial</div>
                  <div style={{ fontSize: 14, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{fmtF(d.montant_initial)}</div>
                </div>
                {d.date_fin_prevue && (
                  <div>
                    <div style={{ fontSize: 11, color: '#7A7A7A', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 2 }}>Soldé le</div>
                    <div style={{ fontSize: 14, fontWeight: 500 }}>{fmtDate(d.date_fin_prevue)}</div>
                  </div>
                )}
              </div>
              <div style={{ fontSize: 12, color: '#7A7A7A', marginTop: 8 }}>Réf. {d.reference}</div>
            </div>
          ))}
        </div>
      )}

    </div>
  );
}

// ─── DetailDemande (vue generique lecture) ────────────────────────────────────
function DetailDemande({ id, token, onBack }) {
  const [data, setData] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };
  useEffect(() => {
    axios.get(`${API}/api/prets-sogas/demandes/${id}`, { headers }).then(r => setData(r.data)).catch(() => {});
  }, [id]);
  if (!data) return <div style={{ padding: 60, textAlign: 'center', color: C.ink3 }}>Chargement...</div>;
  const { demande, historique } = data;
  return (
    <div>
      <button onClick={onBack} style={BTN_G}><IcoBack /> Retour</button>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', margin: '14px 0 4px', flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: C.ink }}>{demande.prenom} {demande.nom}</h2>
          <p style={{ margin: '4px 0 0', color: C.ink3, fontSize: 13 }}>{demande.numero_dossier} · Soumis le {fmtDate(demande.created_at)}</p>
        </div>
        <BadgeStatut statut={demande.statut} />
      </div>
      <Timeline5 statut={demande.statut} />
      <SuiviDemande suivi={demande.suivi} circuit="ouvert" style={{ margin: '0 0 14px' }} />
      <div style={CARD}>
        <div style={CARD_TITLE}><IcoDoc /> Détails du prêt</div>
        <KVGrid cols={2} items={[
          { k: 'Montant demandé',   v: fmt(demande.montant_demande) },
          { k: 'Durée',             v: `${demande.duree_mois} mois` },
          { k: 'Mensualité',        v: fmt(demande.mensualite_retenue || Math.round(demande.montant_demande / demande.duree_mois)) },
          { k: 'Motif',             v: demande.motif },
        ]} />
        {demande.montant_accorde && <CheckRow k="Montant accordé" v={fmt(demande.montant_accorde)} noTag />}
        {demande.date_debut_remboursement && <CheckRow k="Début remboursement" v={fmtDate(demande.date_debut_remboursement)} noTag />}
        {demande.reference_virement && <CheckRow k="Référence virement" v={demande.reference_virement} noTag />}
        {demande.motif_refus && (
          <div style={{ background: C.redSoft, borderRadius: 8, padding: '10px 12px', fontSize: 13, color: C.red, marginTop: 8 }}>
            <strong>Motif de refus : </strong>{demande.motif_refus}
          </div>
        )}
        {demande.commentaire_dg && <div style={{ fontSize: 13, color: C.ink2, marginTop: 8, fontStyle: 'italic' }}>Commentaire DG : "{demande.commentaire_dg}"</div>}
      </div>
      {demande.statut === 'actif' && (
        <TableauAmortissement
          montant={demande.montant_accorde}
          mensualite={demande.mensualite_retenue}
          dateDebut={demande.date_debut_remboursement}
          duree={demande.duree_mois}
        />
      )}
      {historique?.length > 0 && (
        <div style={CARD}>
          <div style={CARD_TITLE}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 1 0 3-6.7"/><path d="M3 4v5h5"/></svg>
            Historique
          </div>
          {historique.map((h, i) => (
            <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 12, alignItems: 'flex-start' }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: C.green, marginTop: 5, flexShrink: 0 }} />
              <div>
                <div style={{ fontSize: 12, color: C.ink3 }}>{fmtDate(h.created_at)} — {h.acteur_nom || 'Systeme'}</div>
                <div style={{ fontSize: 13, fontWeight: 600, textTransform: 'capitalize' }}>{h.action?.replace(/_/g, ' ')}</div>
                {h.commentaire && <div style={{ fontSize: 12, color: C.ink2 }}>{h.commentaire}</div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── VueDGListe ───────────────────────────────────────────────────────────────
function VueDGListe({ token, seuil, onVoirDetail, onAction }) {
  const [demandes, setDemandes]     = useState([]);
  const [loading, setLoading]       = useState(false);
  const [err, setErr]               = useState('');
  const [msgMap, setMsgMap]         = useState({});
  const [refusPending, setRefusPending] = useState(null);
  const [motifRefus, setMotifRefus] = useState('');
  const [errMotif, setErrMotif]     = useState('');
  const headers = { Authorization: `Bearer ${token}` };

  const charger = useCallback(() => {
    axios.get(`${API}/api/prets-sogas/demandes?statut=soumis`, { headers })
      .then(r => setDemandes(toArray(r.data.demandes || r.data)))
      .catch(() => {});
  }, [token]);

  useEffect(() => { charger(); }, [charger]);

  const doAction = async (id, endpoint, body = {}) => {
    setErr(''); setLoading(true);
    try {
      const r = await axios.post(`${API}/api/prets-sogas/demandes/${id}/${endpoint}`, body, { headers });
      setMsgMap(m => ({ ...m, [id]: r.data.message }));
      charger();
    } catch (ex) { setErr(ex.response?.data?.message || 'Erreur'); }
    finally { setLoading(false); }
  };

  return (
    <div>
      <style>{`
        .sogas-dg-desktop { display: block; }
        .sogas-dg-cards { display: none; }
        .sogas-dg-bandeau { background: #E8F5E9; border: 1px solid #A5D6A7; color: #1B5E20; font-size: 13px; padding: 10px 16px; border-radius: 8px; margin-bottom: 16px; font-weight: 600; }
        .sogas-dg-modal-content { width: 420px; max-width: 90vw; background: #fff; border-radius: 12px; padding: 20px 24px; box-shadow: 0 20px 60px rgba(0,0,0,0.2); }
        .sogas-dg-modal-textarea { min-height: 80px; }
        .sogas-dg-modal-btns { display: flex; gap: 10px; justify-content: flex-end; margin-top: 16px; }
        .sogas-dg-modal-btn { box-sizing: border-box; }
        @media (max-width: 767px) {
          .sogas-dg-desktop { display: none; }
          .sogas-dg-cards { display: block; }
          .sogas-dg-modal-content { width: calc(100% - 32px); }
          .sogas-dg-modal-textarea { min-height: 100px; }
          .sogas-dg-modal-btns { flex-direction: column-reverse; }
          .sogas-dg-modal-btn { min-height: 44px; }
        }
      `}</style>
      {/* Modale refus DG */}
      {refusPending && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="sogas-dg-modal-content">
            <h3 style={{ margin: '0 0 14px', fontSize: 16, fontWeight: 700, color: '#1A1A1A' }}>Refuser cette demande ?</h3>
            <div style={{ background: '#F7F7F7', borderRadius: 8, padding: '10px 14px', marginBottom: 16 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#1A1A1A', marginBottom: 4 }}>{refusPending.prenom} {refusPending.nom}</div>
              {refusPending.numero_dossier && (
                <div style={{ fontSize: 12, color: '#7A7A7A', marginBottom: 2 }}>Dossier n° {refusPending.numero_dossier}</div>
              )}
              <div style={{ fontSize: 13, color: '#4A4A4A' }}>Montant demandé : <strong>{fmt(refusPending.montant_demande)}</strong></div>
            </div>
            <label style={{ fontSize: 12.5, fontWeight: 600, color: '#4A4A4A', display: 'block', marginBottom: 6 }}>Motif du refus *</label>
            <textarea
              value={motifRefus}
              onChange={e => { setMotifRefus(e.target.value); if (errMotif) setErrMotif(''); }}
              placeholder="Préciser le motif du refus..."
              className="sogas-dg-modal-textarea"
              style={{ width: '100%', padding: '10px 12px', border: `1px solid ${errMotif ? C.red : C.line}`, borderRadius: 8, fontSize: 13, fontFamily: FONT, boxSizing: 'border-box', resize: 'vertical', outline: 'none' }}
            />
            {errMotif && <div style={{ fontSize: 12, color: C.red, marginTop: 4 }}>{errMotif}</div>}
            <div className="sogas-dg-modal-btns">
              <button
                className="sogas-dg-modal-btn"
                onClick={() => setRefusPending(null)}
                style={{ padding: '9px 18px', borderRadius: 8, border: '1px solid #CCC', background: '#F5F5F5', color: '#555', fontSize: 13, cursor: 'pointer', fontWeight: 600, fontFamily: FONT }}
              >Annuler</button>
              <button
                className="sogas-dg-modal-btn"
                disabled={loading}
                onClick={() => {
                  if (motifRefus.trim().length < 10) { setErrMotif('Le motif doit faire au moins 10 caractères.'); return; }
                  doAction(refusPending.id, 'refuser', { motif_refus: motifRefus.trim() });
                  setRefusPending(null);
                }}
                style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: C.red, color: '#fff', fontSize: 13, cursor: 'pointer', fontWeight: 600, fontFamily: FONT }}
              >Confirmer le refus</button>
            </div>
          </div>
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Prets SOGAS — Validation DG</h2>
          <p style={{ margin: '4px 0 0', color: C.ink3, fontSize: 14 }}>Demandes a examiner et decider</p>
        </div>
        {demandes.length > 0 && <BadgeStatut statut="soumis" />}
      </div>
      {demandes.length > 0 && (
        <div className="sogas-dg-bandeau">{demandes.length} dossier(s) en attente de décision</div>
      )}
      {err && <div style={{ background: C.redSoft, color: C.red, padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: 13 }}>{err}</div>}
      {demandes.length === 0 ? (
        <div style={{ textAlign: 'center', color: C.ink3, padding: 60, fontSize: 14 }}>Aucune demande en attente de validation</div>
      ) : (
        <>
          {/* Vue desktop — ReqCard détaillée */}
          <div className="sogas-dg-desktop">
            {demandes.map(d => (
              <div key={d.id}>
                {msgMap[d.id] && <div style={{ background: C.greenSoft, color: C.green, padding: '8px 12px', borderRadius: 8, marginBottom: 8, fontSize: 13 }}>{msgMap[d.id]}</div>}
                <ReqCard d={d} seuil={seuil} onVoir={() => onVoirDetail(d.id)} actions={
                  <>
                    <button style={BTN_W} disabled={loading} onClick={() => onVoirDetail(d.id, 'modifier')}>Modifier et approuver</button>
                    <button style={BTN_D} disabled={loading} onClick={() => { setRefusPending(d); setMotifRefus(''); setErrMotif(''); }}>Refuser</button>
                    <button style={BTN_P} disabled={loading} onClick={() => doAction(d.id, 'approuver', {})}>
                      <IcoCheck /> Approuver
                    </button>
                  </>
                } />
              </div>
            ))}
          </div>
          {/* Vue mobile — cartes décisionnelles */}
          <div className="sogas-dg-cards">
            {demandes.map(d => {
              const montant = parseFloat(d.montant_accorde || d.montant_demande) || 0;
              const duree   = parseInt(d.duree_mois) || 1;
              const mens    = d.mensualite_retenue ? parseFloat(d.mensualite_retenue) : Math.round(montant / duree);
              const elevé   = montant >= 500000;
              return (
                <div key={d.id} style={{
                  background: '#fff',
                  border: `0.5px solid ${C.line}`,
                  borderLeft: elevé ? `3px solid ${C.orange}` : `0.5px solid ${C.line}`,
                  borderRadius: R,
                  marginBottom: 10,
                  overflow: 'hidden',
                }}>
                  {msgMap[d.id] && (
                    <div style={{ background: C.greenSoft, color: C.green, padding: '8px 14px', fontSize: 13 }}>{msgMap[d.id]}</div>
                  )}
                  {/* Identité */}
                  <div style={{ padding: '14px 16px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                    <div style={{ display: 'flex', gap: 12, alignItems: 'center', flex: 1, minWidth: 0 }}>
                      <AvatarCircle prenom={d.prenom} nom={d.nom} size={40} />
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 15, fontWeight: 700, color: C.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.prenom} {d.nom}</div>
                        <div style={{ fontSize: 12, color: C.ink3, marginTop: 2 }}>
                          {d.numero_dossier ? `Dossier n° ${d.numero_dossier}` : 'Dossier en attente'}
                          {d.site_nom ? ` · ${d.site_nom}` : ''}
                        </div>
                      </div>
                    </div>
                    <BadgeStatut statut={d.statut} />
                  </div>
                  {/* Métriques 3 colonnes */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', padding: '8px 16px 10px', borderTop: `1px solid ${C.line2}`, borderBottom: `1px solid ${C.line2}` }}>
                    {[['MONTANT', fmt(montant)], ['MENSUALITE', fmt(mens)], ['DUREE', `${d.duree_mois} mois`]].map(([label, val], i) => (
                      <div key={label} style={{ textAlign: i === 0 ? 'left' : i === 1 ? 'center' : 'right', padding: '4px 0' }}>
                        <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.05em', color: C.ink3, fontWeight: 600, marginBottom: 3 }}>{label}</div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: C.ink, fontVariantNumeric: 'tabular-nums' }}>{val}</div>
                      </div>
                    ))}
                  </div>
                  {/* Actions */}
                  <div style={{ padding: '10px 16px 14px', display: 'flex', gap: 8 }}>
                    <button
                      style={{ flex: 1, minHeight: 42, borderRadius: 8, border: `1px solid ${C.line}`, background: '#F5F5F5', color: C.ink2, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: FONT }}
                      onClick={() => onVoirDetail(d.id)}
                    >Voir dossier</button>
                    <button
                      style={{ flex: 1, minHeight: 42, borderRadius: 8, border: '1px solid #A5D6A7', background: C.greenSoft, color: C.green, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: FONT }}
                      disabled={loading}
                      onClick={() => doAction(d.id, 'approuver', {})}
                    >Approuver</button>
                    <button
                      style={{ flex: 1, minHeight: 42, borderRadius: 8, border: '1px solid #E6C7C7', background: C.redSoft, color: C.red, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: FONT }}
                      disabled={loading}
                      onClick={() => { setRefusPending(d); setMotifRefus(''); setErrMotif(''); }}
                    >Refuser</button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// ─── VueDGDetail ─────────────────────────────────────────────────────────────
function VueDGDetail({ id, token, onBack, seuil }) {
  const [data, setData]     = useState(null);
  const [decision, setDec]  = useState('approuver');
  const [form, setForm]     = useState({});
  const [loading, setLoad]  = useState(false);
  const [msg, setMsg]       = useState('');
  const [err, setErr]       = useState('');
  const [pretsAnts, setPrA] = useState([]);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/api/prets-sogas/demandes/${id}`, { headers }).then(r => { setData(r.data); });
    axios.get(`${API}/api/prets-sogas/demandes?employe_id_hint=${id}&statut=solde`, { headers })
      .then(r => setPrA(toArray(r.data.demandes || r.data))).catch(() => {});
  }, [id]);

  const doAction = async (endpoint, body) => {
    setErr(''); setMsg(''); setLoad(true);
    try {
      const r = await axios.post(`${API}/api/prets-sogas/demandes/${id}/${endpoint}`, body, { headers });
      setMsg(r.data.message);
      const r2 = await axios.get(`${API}/api/prets-sogas/demandes/${id}`, { headers });
      setData(r2.data);
    } catch (ex) { setErr(ex.response?.data?.message || 'Erreur'); }
    finally { setLoad(false); }
  };

  const confirmer = () => {
    if (decision === 'refuser') {
      if (!form.motif_refus) { setErr('Le motif du refus est requis'); return; }
      doAction('refuser', { motif_refus: form.motif_refus });
    } else if (decision === 'modifier') {
      doAction('approuver', { commentaire: form.commentaire, montant_modifie: form.montant_modifie, duree_modifiee: form.duree_modifiee });
    } else {
      doAction('approuver', { commentaire: form.commentaire });
    }
  };

  if (!data) return <div style={{ padding: 60, textAlign: 'center', color: C.ink3 }}>Chargement...</div>;
  const { demande, historique } = data;
  const salaire  = parseFloat(demande.salaire_base) || 0;
  const charge   = parseFloat(demande.charge_actuelle) || 0;
  const montant  = parseFloat(demande.montant_demande) || 0;
  const duree    = parseInt(demande.duree_mois) || 1;
  const mens     = Math.round(montant / duree);
  const total    = charge + mens;
  const pct      = salaire > 0 ? Math.round((total / salaire) * 100) : 0;
  const warn     = pct > (seuil || 50);
  const ancMois  = demande.date_embauche ? Math.floor((Date.now() - new Date(demande.date_embauche)) / (1000*60*60*24*30.44)) : 0;
  const peutDecider = ['soumis'].includes(demande.statut);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <button onClick={onBack} style={{ ...BTN_G, marginBottom: 10 }}><IcoBack /> Prets SOGAS — DG</button>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>Dossier de demande — {demande.prenom} {demande.nom}</h2>
          <p style={{ margin: '4px 0 0', color: C.ink3, fontSize: 13 }}>Soumis le {fmtDate(demande.created_at)} · Demande n° {demande.numero_dossier}</p>
        </div>
        <BadgeStatut statut={demande.statut} />
      </div>

      <Timeline5 statut={demande.statut} />
      <SuiviDemande suivi={demande.suivi} circuit="ouvert" style={{ margin: '0 0 14px' }} />
      {msg && <div style={{ background: C.greenSoft, color: C.green, padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: 13 }}>{msg}</div>}
      {err && <div style={{ background: C.redSoft, color: C.red, padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: 13 }}>{err}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.15fr) minmax(0,1fr)', gap: 16 }}>
        {/* Gauche */}
        <div>
          <div style={CARD}>
            <div style={CARD_TITLE}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4"/><path d="M4 21c1-4 4-6 8-6s7 2 8 6"/></svg>
              Fiche employe
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 20px' }}>
              {[
                ['Nom complet',    `${demande.prenom} ${demande.nom}`],
                ['Matricule',      demande.matricule],
                ['Contrat',        `${demande.type_contrat || '—'}`],
                ['Anciennete',     ancLabel(ancMois)],
                ['Salaire de base', fmt(salaire)],
                ['Site',           demande.site_nom || '—'],
              ].map(([k, v]) => (
                <div key={k}>
                  <div style={{ fontSize: 11, color: C.ink3, textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 2 }}>{k}</div>
                  <div style={{ fontSize: 13.5, fontWeight: 600 }}>{v || '—'}</div>
                </div>
              ))}
            </div>
          </div>

          <div style={CARD}>
            <div style={CARD_TITLE}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 1 0 3-6.7"/><path d="M3 4v5h5"/></svg>
              Prets SOGAS anterieurs
              <span style={{ marginLeft: 'auto', fontSize: 12, color: C.ink3, fontWeight: 500 }}>{pretsAnts.length} soldes</span>
            </div>
            {pretsAnts.length === 0 ? (
              <div style={{ color: C.ink3, fontSize: 13, textAlign: 'center', padding: '8px 0' }}>Aucun prêt antérieur</div>
            ) : (
              <div className="sogas-pants-wrap">
                <table className="sogas-pants-table" style={{ borderCollapse: 'collapse' }}>
                  <thead><tr>{['Année','Montant','Durée','Statut'].map(h => <th key={h} style={{ textAlign: 'left', fontSize: 11, textTransform: 'uppercase', letterSpacing: '.05em', color: C.ink3, padding: '8px 0 6px', borderBottom: `1px solid ${C.line}`, fontWeight: 600 }}>{h}</th>)}</tr></thead>
                  <tbody>{pretsAnts.map(p => <tr key={p.id}>{[new Date(p.created_at).getFullYear(), fmt(p.montant_demande), `${p.duree_mois} mois`, <BadgeStatut key="s" statut={p.statut} />].map((v, i) => <td key={i} style={{ padding: '8px 0', fontSize: 13, borderBottom: `1px solid ${C.line2}`, fontVariantNumeric: 'tabular-nums' }}>{v}</td>)}</tr>)}</tbody>
                </table>
                <div className="sogas-pants-cards">
                  {pretsAnts.map((p, i) => (
                    <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: i < pretsAnts.length - 1 ? `1px solid ${C.line2}` : 'none' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{fmt(p.montant_demande)}</div>
                        <div style={{ fontSize: 12, color: C.ink3 }}>{new Date(p.created_at).getFullYear()} · {p.duree_mois} mois</div>
                      </div>
                      <BadgeStatut statut={p.statut} />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div style={CARD}>
            <div style={CARD_TITLE}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 1 0 0 7H14a3.5 3.5 0 1 1 0 7H6"/></svg>
              Solvabilité consolidée
            </div>
            <SolvBar salaire={salaire} charge={charge} mensExtra={mens} seuil={seuil || 50} />
          </div>

          <div style={CARD}>
            <div style={CARD_TITLE}><IcoDoc /> Détails de la demande</div>
            <KVGrid cols={2} items={[
              { k: 'Montant demandé', v: fmt(montant) },
              { k: 'Durée',           v: `${duree} mois` },
              { k: 'Mensualité',      v: fmt(mens) },
              { k: 'Motif',          v: demande.motif },
            ]} />
          </div>
        </div>

        {/* Droite — decision */}
        <div>
          <div style={CARD}>
            <div style={CARD_TITLE}>
              <IcoCheck />
              Decision du DG
            </div>

            {peutDecider ? (
              <>
                {[
                  { val: 'approuver', title: 'Approuver tel quel', sub: `Montant ${fmt(montant)} sur ${duree} mois · mensualité ${fmt(mens)}`, color: C.green },
                  { val: 'modifier',  title: 'Approuver avec modifications', sub: 'Ajuster le montant ou la durée', color: C.indigo },
                  { val: 'refuser',   title: 'Refuser la demande', sub: "Le motif sera communiqué à l'employé", color: C.red },
                ].map(opt => (
                  <label key={opt.val} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '12px 14px', border: `1px solid ${decision === opt.val ? (opt.val === 'refuser' ? C.red : C.green) : C.line}`, borderRadius: 10, cursor: 'pointer', background: decision === opt.val ? (opt.val === 'refuser' ? '#FFF5F5' : '#F5FBF5') : '#fff', marginBottom: 8 }}>
                    <input type="radio" name={`dec_${id}`} checked={decision === opt.val} onChange={() => setDec(opt.val)} style={{ marginTop: 2, accentColor: opt.color, flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 13.5, color: opt.val === 'refuser' ? C.red : C.ink }}>{opt.title}</div>
                      <div style={{ fontSize: 12, color: C.ink3, marginTop: 2 }}>{opt.sub}</div>
                      {decision === 'modifier' && opt.val === 'modifier' && (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
                          <div><label style={LABEL_ST}>Nouveau montant (FCFA)</label><input type="number" placeholder="FCFA" value={form.montant_modifie || ''} onChange={e => setForm(f => ({ ...f, montant_modifie: e.target.value }))} style={{ ...INPUT_ST, marginTop: 4 }} /></div>
                          <div><label style={LABEL_ST}>Nouvelle duree (mois)</label><input type="number" placeholder="Ex. 30" value={form.duree_modifiee || ''} onChange={e => setForm(f => ({ ...f, duree_modifiee: e.target.value }))} style={{ ...INPUT_ST, marginTop: 4 }} /></div>
                        </div>
                      )}
                      {decision === 'refuser' && opt.val === 'refuser' && (
                        <div style={{ marginTop: 8 }}>
                          <label style={LABEL_ST}>Motif du refus <span style={{ color: C.red }}>*</span></label>
                          <textarea rows={3} placeholder="Expliquez le motif..." value={form.motif_refus || ''} onChange={e => setForm(f => ({ ...f, motif_refus: e.target.value }))} style={{ ...INPUT_ST, marginTop: 4, resize: 'vertical' }} />
                        </div>
                      )}
                    </div>
                  </label>
                ))}

                <div style={{ marginTop: 6 }}>
                  <label style={LABEL_ST}>Commentaire DG <span style={{ fontWeight: 400, color: C.ink4 }}>(optionnel)</span></label>
                  <textarea rows={2} placeholder="Note a transmettre a l'equipe RH..." value={form.commentaire || ''} onChange={e => setForm(f => ({ ...f, commentaire: e.target.value }))} style={{ ...INPUT_ST, marginTop: 4, resize: 'vertical' }} />
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 12, borderTop: `1px solid ${C.line2}`, marginTop: 12 }}>
                  <button disabled={loading || (decision === 'refuser' && !form.motif_refus)} onClick={confirmer}
                    style={decision === 'refuser' ? BTN_D : BTN_P}>
                    {loading ? 'En cours...' : 'Confirmer la decision'}
                  </button>
                </div>
              </>
            ) : (
              <div style={{ color: C.ink3, fontSize: 13, textAlign: 'center', padding: 16 }}>
                Decision deja rendue — statut : <BadgeStatut statut={demande.statut} />
                {demande.motif_refus && <div style={{ color: C.red, marginTop: 8 }}>Motif : {demande.motif_refus}</div>}
                {demande.commentaire_dg && <div style={{ color: C.ink2, marginTop: 8, fontStyle: 'italic' }}>"{demande.commentaire_dg}"</div>}
              </div>
            )}
          </div>

          {historique?.length > 0 && (
            <div style={CARD}>
              <div style={CARD_TITLE}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 1 0 3-6.7"/><path d="M3 4v5h5"/></svg>
                Historique
              </div>
              {historique.map((h, i) => (
                <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 10, alignItems: 'flex-start' }}>
                  <div style={{ width: 7, height: 7, borderRadius: '50%', background: C.green, marginTop: 5, flexShrink: 0 }} />
                  <div>
                    <div style={{ fontSize: 12, color: C.ink3 }}>{fmtDate(h.created_at)} — {h.acteur_nom || 'Systeme'}</div>
                    <div style={{ fontSize: 13, fontWeight: 600, textTransform: 'capitalize' }}>{h.action?.replace(/_/g, ' ')}</div>
                    {h.commentaire && <div style={{ fontSize: 12, color: C.ink2 }}>{h.commentaire}</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── VueRHListe ───────────────────────────────────────────────────────────────
function VueRHListe({ token, seuil, onVoirDetail }) {
  const [demandes, setDemandes] = useState([]);
  const headers = { Authorization: `Bearer ${token}` };

  const charger = useCallback(() => {
    axios.get(`${API}/api/prets-sogas/demandes?statut=approuve_dg`, { headers })
      .then(r => setDemandes(toArray(r.data.demandes || r.data)))
      .catch(() => {});
  }, [token]);

  useEffect(() => { charger(); }, [charger]);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Prets SOGAS — Traitement RH</h2>
          <p style={{ margin: '4px 0 0', color: C.ink3, fontSize: 14 }}>Dossiers approuves par la DG a finaliser</p>
        </div>
        {demandes.length > 0 && <BadgeStatut statut="approuve_dg" />}
      </div>
      {demandes.length === 0 ? (
        <div style={{ textAlign: 'center', color: C.ink3, padding: 60, fontSize: 14 }}>Aucun dossier a traiter</div>
      ) : (
        demandes.map(d => (
          <ReqCard key={d.id} d={d} seuil={seuil} onVoir={null} actions={
            <button style={BTN_P} onClick={() => onVoirDetail(d.id)}>Verifier et traiter <IcoArrow /></button>
          } />
        ))
      )}
    </div>
  );
}

// ─── VueRHDetail ─────────────────────────────────────────────────────────────
function VueRHDetail({ id, token, onBack, seuil }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoad] = useState(false);
  const [msg, setMsg]   = useState('');
  const [err, setErr]   = useState('');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/api/prets-sogas/demandes/${id}`, { headers }).then(r => { setData(r.data); });
  }, [id]);

  const doAction = async (endpoint, body) => {
    setErr(''); setMsg(''); setLoad(true);
    try {
      const r = await axios.post(`${API}/api/prets-sogas/demandes/${id}/${endpoint}`, body, { headers });
      setMsg(r.data.message);
      const r2 = await axios.get(`${API}/api/prets-sogas/demandes/${id}`, { headers });
      setData(r2.data);
    } catch (ex) { setErr(ex.response?.data?.message || 'Erreur'); }
    finally { setLoad(false); }
  };

  if (!data) return <div style={{ padding: 60, textAlign: 'center', color: C.ink3 }}>Chargement...</div>;
  const { demande } = data;
  const salaire    = parseFloat(demande.salaire_base)    || 0;
  const charge     = parseFloat(demande.charge_actuelle) || 0;
  const montantAcc = parseFloat(demande.montant_accorde || demande.montant_demande) || 0;
  const duree      = parseInt(demande.duree_mois) || 1;
  const duree_rh   = parseInt(form.duree_mois) || duree;
  const mens       = Math.round(montantAcc / duree_rh);

  const canTraiter   = demande.statut === 'approuve_dg';
  const canTransmettre = demande.statut === 'traite_rh';

  let dateFin = '';
  if (form.date_debut_remboursement) {
    const df = new Date(form.date_debut_remboursement);
    df.setMonth(df.getMonth() + duree);
    dateFin = df.toLocaleDateString('fr-FR');
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <button onClick={onBack} style={{ ...BTN_G, marginBottom: 10 }}><IcoBack /> Traitement RH</button>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>Traitement — {demande.prenom} {demande.nom}</h2>
          <p style={{ margin: '4px 0 0', color: C.ink3, fontSize: 13 }}>Verification finale et generation du bon de pret</p>
        </div>
        <BadgeStatut statut={demande.statut} />
      </div>

      <Timeline5 statut={demande.statut} />
      <SuiviDemande suivi={demande.suivi} circuit="ouvert" style={{ margin: '0 0 14px' }} />
      {msg && <div style={{ background: C.greenSoft, color: C.green, padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: 13 }}>{msg}</div>}
      {err && <div style={{ background: C.redSoft, color: C.red, padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: 13 }}>{err}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.15fr) minmax(0,1fr)', gap: 16 }}>
        {/* Gauche */}
        <div>
          <div style={CARD}>
            <div style={CARD_TITLE}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 1 0 0 7H14a3.5 3.5 0 1 1 0 7H6"/></svg>
              Solvabilité recalculée
            </div>
            <SolvBar salaire={salaire} charge={charge} mensExtra={mens} seuil={seuil || 50} />
            {demande.commentaire_dg && (
              <div style={{ fontSize: 12.5, color: C.ink2, fontStyle: 'italic', marginTop: 4 }}>
                Commentaire DG : "{demande.commentaire_dg}"
              </div>
            )}
          </div>

          {canTraiter && (
            <div style={CARD}>
              <div style={CARD_TITLE}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>
                Paramètres du prêt
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div><label style={LABEL_ST}>Date de début du remboursement</label><input type="date" value={form.date_debut_remboursement || ''} onChange={e => setForm(f => ({ ...f, date_debut_remboursement: e.target.value }))} style={{ ...INPUT_ST, marginTop: 4 }} /></div>
                <div><label style={LABEL_ST}>Nombre de mensualités</label><input type="number" value={form.duree_mois || duree} onChange={e => setForm(f => ({ ...f, duree_mois: parseInt(e.target.value) }))} style={{ ...INPUT_ST, marginTop: 4 }} /></div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div>
                  <label style={LABEL_ST}>Mensualité calculée</label>
                  <div style={{ ...INPUT_ST, marginTop: 4, background: '#F0F7F0', color: C.green, fontWeight: 700, display: 'flex', alignItems: 'center' }}>
                    {new Intl.NumberFormat('fr-FR').format(mens)} FCFA
                    <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 400, color: C.ink3 }}>auto = montant ÷ durée</span>
                  </div>
                </div>
                <div><label style={LABEL_ST}>Fin prevue</label><input type="text" value={dateFin} disabled style={{ ...INPUT_ST, marginTop: 4, background: '#FAFAFA', color: C.ink3 }} /></div>
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={LABEL_ST}>Note RH <span style={{ fontWeight: 400, color: C.ink4 }}>(interne)</span></label>
                <textarea rows={2} placeholder="Remarques pour la comptabilite..." value={form.notes_rh || ''} onChange={e => setForm(f => ({ ...f, notes_rh: e.target.value }))} style={{ ...INPUT_ST, marginTop: 4, resize: 'vertical' }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 12, borderTop: `1px solid ${C.line2}` }}>
                <button disabled={loading || !form.date_debut_remboursement}
                  onClick={() => doAction('traiter', { montant_accorde: montantAcc, duree_mois: duree_rh, date_debut_remboursement: form.date_debut_remboursement, notes_rh: form.notes_rh })}
                  style={BTN_P}>{loading ? 'Traitement...' : 'Valider le traitement RH'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Droite — apercu bon + transmettre */}
        <div>
          <div style={CARD}>
            <div style={CARD_TITLE}><IcoDoc /> Apercu du bon officiel <span style={{ marginLeft: 'auto', fontSize: 12, color: C.ink3, fontWeight: 500 }}>Previsualisation</span></div>
            <DocPretPreview demande={{ ...demande, date_debut_remboursement: form.date_debut_remboursement || demande.date_debut_remboursement }} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, paddingTop: 14, marginTop: 14, borderTop: `1px solid ${C.line2}`, flexWrap: 'wrap' }}>
              {canTransmettre && (
                <button disabled={loading} onClick={() => doAction('transmettre', {})} style={BTN_P}>
                  Transmettre a la Comptabilite <IcoArrow />
                </button>
              )}
              {!canTraiter && !canTransmettre && (
                <div style={{ color: C.ink3, fontSize: 13 }}>Statut : <BadgeStatut statut={demande.statut} /></div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── VueComptaListe ───────────────────────────────────────────────────────────
function VueComptaListe({ token, seuil, onVoirDetail }) {
  const [demandes, setDemandes]  = useState([]);
  const [historique, setHist]    = useState([]);
  const headers = { Authorization: `Bearer ${token}` };

  const charger = useCallback(() => {
    axios.get(`${API}/api/prets-sogas/demandes?statut=transmis_compta`, { headers })
      .then(r => setDemandes(toArray(r.data.demandes || r.data)))
      .catch(() => {});
    axios.get(`${API}/api/prets-sogas/demandes?statut=actif`, { headers })
      .then(r => setHist(toArray(r.data.demandes || r.data).slice(0, 10)))
      .catch(() => {});
  }, [token]);

  useEffect(() => { charger(); }, [charger]);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Prêts SOGAS — Déblocage des fonds</h2>
          <p style={{ margin: '4px 0 0', color: C.ink3, fontSize: 14 }}>Dossiers transmis par le service RH</p>
        </div>
        {demandes.length > 0 && <BadgeStatut statut="transmis_compta" />}
      </div>

      {demandes.length === 0 ? (
        <div style={{ textAlign: 'center', color: C.ink3, padding: 40, fontSize: 14 }}>Aucun dossier en attente de déblocage</div>
      ) : (
        demandes.map(d => (
          <ReqCard key={d.id} d={d} seuil={seuil} onVoir={null} actions={
            <button style={BTN_P} onClick={() => onVoirDetail(d.id)}>Confirmer le deblocage <IcoArrow /></button>
          } />
        ))
      )}

      {historique.length > 0 && (
        <div style={{ ...CARD, marginTop: 18 }}>
          <div style={CARD_TITLE}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 1 0 3-6.7"/><path d="M3 4v5h5"/></svg>
            Historique comptabilite
            <span style={{ marginLeft: 'auto', fontSize: 12, color: C.ink3, fontWeight: 500 }}>Derniers deblocages</span>
          </div>
          <div className="sogas-histo-wrap">
            <table className="sogas-histo-table" style={{ borderCollapse: 'collapse', minWidth: 600 }}>
              <thead>
                <tr>{['Date', 'Employe', 'Montant', 'Reference', 'Statut', ''].map((h, i) => (
                  <th key={i} style={{ textAlign: i === 5 ? 'right' : 'left', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: C.ink3, padding: '10px 12px', borderBottom: `1px solid ${C.line}`, background: '#FAFAFA' }}>{h}</th>
                ))}</tr>
              </thead>
              <tbody>
                {historique.map((d, i) => (
                  <tr key={d.id} style={{ borderBottom: i < historique.length - 1 ? `1px solid ${C.line2}` : 'none' }}>
                    <td style={{ padding: '12px', fontSize: 13, fontVariantNumeric: 'tabular-nums', color: C.ink2 }}>{fmtDate(d.date_deblocage || d.updated_at)}</td>
                    <td style={{ padding: '12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <AvatarCircle prenom={d.prenom} nom={d.nom} size={28} />
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 13 }}>{d.prenom} {d.nom}</div>
                          <div style={{ fontSize: 11.5, color: C.ink3, fontVariantNumeric: 'tabular-nums' }}>{d.matricule}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '12px', fontSize: 13, fontVariantNumeric: 'tabular-nums', fontWeight: 500 }}>{fmt(d.montant_accorde || d.montant_demande)}</td>
                    <td style={{ padding: '12px', fontSize: 13, fontVariantNumeric: 'tabular-nums', color: C.ink3 }}>{d.reference_virement || '—'}</td>
                    <td style={{ padding: '12px' }}><BadgeStatut statut={d.statut} /></td>
                    <td style={{ padding: '12px', textAlign: 'right' }}>
                      <button style={BTN_SM} onClick={() => onVoirDetail(d.id)}>Voir</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="sogas-histo-cards">
              {historique.map((d, i) => (
                <div key={d.id} style={{ padding: '12px 0', borderBottom: i < historique.length - 1 ? `1px solid ${C.line2}` : 'none' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <AvatarCircle prenom={d.prenom} nom={d.nom} size={28} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{d.prenom} {d.nom}</div>
                      <div style={{ fontSize: 11.5, color: C.ink3 }}>{d.matricule}</div>
                    </div>
                    <BadgeStatut statut={d.statut} />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                    <div>
                      <div style={{ fontSize: 10, textTransform: 'uppercase', color: C.ink4, letterSpacing: '.05em', marginBottom: 2 }}>Date</div>
                      <div style={{ fontSize: 13, fontVariantNumeric: 'tabular-nums', color: C.ink2 }}>{fmtDate(d.date_deblocage || d.updated_at)}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 10, textTransform: 'uppercase', color: C.ink4, letterSpacing: '.05em', marginBottom: 2 }}>Montant</div>
                      <div style={{ fontSize: 13, fontVariantNumeric: 'tabular-nums', fontWeight: 500 }}>{fmt(d.montant_accorde || d.montant_demande)}</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ flex: 1, fontSize: 12, color: C.ink3 }}>{d.reference_virement || '—'}</div>
                    <button style={{ ...BTN_SM, minHeight: 40 }} onClick={() => onVoirDetail(d.id)}>Voir</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── VueComptaDetail ─────────────────────────────────────────────────────────
function VueComptaDetail({ id, token, onBack }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ reference_virement: '', notes_compta: '' });
  const [loading, setLoad] = useState(false);
  const [msg, setMsg]   = useState('');
  const [err, setErr]   = useState('');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/api/prets-sogas/demandes/${id}`, { headers }).then(r => setData(r.data));
  }, [id]);

  const debloquer = async () => {
    setErr(''); setMsg(''); setLoad(true);
    try {
      const r = await axios.post(`${API}/api/prets-sogas/demandes/${id}/debloquer`, form, { headers });
      setMsg(r.data.message);
      const r2 = await axios.get(`${API}/api/prets-sogas/demandes/${id}`, { headers });
      setData(r2.data);
    } catch (ex) { setErr(ex.response?.data?.message || 'Erreur'); }
    finally { setLoad(false); }
  };

  if (!data) return <div style={{ padding: 60, textAlign: 'center', color: C.ink3 }}>Chargement...</div>;
  const { demande } = data;
  const canDebloquer = ['traite_rh', 'transmis_compta'].includes(demande.statut);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <button onClick={onBack} style={{ ...BTN_G, marginBottom: 10 }}><IcoBack /> Comptabilite</button>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>Confirmation du deblocage</h2>
          <p style={{ margin: '4px 0 0', color: C.ink3, fontSize: 13 }}>Dossier {demande.numero_dossier}</p>
        </div>
        <BadgeStatut statut={demande.statut} />
      </div>

      <Timeline5 statut={demande.statut} />
      <SuiviDemande suivi={demande.suivi} circuit="ouvert" style={{ margin: '0 0 14px' }} />
      {msg && <div style={{ background: C.greenSoft, color: C.green, padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: 13 }}>{msg}</div>}
      {err && <div style={{ background: C.redSoft, color: C.red, padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: 13 }}>{err}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1.15fr)', gap: 16 }}>
        {/* Gauche — resume */}
        <div>
          <div style={CARD}>
            <div style={CARD_TITLE}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="M3 10h18"/></svg>
              Résumé du prêt
            </div>
            <CheckRow k="Bénéficiaire" v={`${demande.prenom} ${demande.nom} · ${demande.matricule}`} noTag />
            <CheckRow k="Site" v={demande.site_nom || '—'} noTag />
            <CheckRow k="Montant" v={fmt(demande.montant_accorde || demande.montant_demande)} noTag />
            <CheckRow k="Mensualité" v={fmt(demande.mensualite_retenue)} noTag />
            <CheckRow k="Durée" v={`${demande.duree_mois} mois`} noTag />
            <CheckRow k="Début remboursement" v={fmtDate(demande.date_debut_remboursement)} noTag />
            <CheckRow k="Type" v="Prêt sans intérêt" noTag />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 0 4px', marginTop: 4, borderTop: `1px solid ${C.line2}`, fontSize: 13 }}>
              <IcoDoc /><span style={{ color: C.ink3 }}>Bon de prêt {demande.numero_dossier}</span>
            </div>
          </div>
        </div>

        {/* Droite — infos deblocage */}
        <div>
          <div style={CARD}>
            <div style={CARD_TITLE}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 1 0 0 7H14a3.5 3.5 0 1 1 0 7H6"/></svg>
              Informations de deblocage
            </div>
            {canDebloquer ? (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                  <div><label style={LABEL_ST}>Date de deblocage</label><input type="date" style={{ ...INPUT_ST, marginTop: 4 }} /></div>
                  <div><label style={LABEL_ST}>Mode de paiement</label>
                    <select style={{ ...INPUT_ST, marginTop: 4 }}>
                      <option>Virement bancaire</option><option>Cheque</option><option>Especes</option>
                    </select>
                  </div>
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={LABEL_ST}>Reference comptable <span style={{ color: C.red }}>*</span></label>
                  <input type="text" placeholder="Ex. VIR-2026-0419-093" value={form.reference_virement} onChange={e => setForm(f => ({ ...f, reference_virement: e.target.value }))} style={{ ...INPUT_ST, marginTop: 4 }} />
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={LABEL_ST}>Preuve de paiement <span style={{ fontWeight: 400, color: C.ink4 }}>(optionnel)</span></label>
                  <div style={{ border: `1.5px dashed ${C.line}`, borderRadius: 10, padding: 18, textAlign: 'center', background: '#FAFAFA', color: C.ink3, fontSize: 13, cursor: 'pointer' }}>
                    <IcoUp />
                    <div style={{ marginTop: 4 }}><strong style={{ color: C.ink, fontWeight: 600 }}>Joindre l'avis de virement</strong></div>
                    <div style={{ fontSize: 11.5, marginTop: 4 }}>PDF, JPG · 5 Mo max</div>
                  </div>
                </div>
                <div style={{ marginBottom: 14 }}>
                  <label style={LABEL_ST}>Note comptable <span style={{ fontWeight: 400, color: C.ink4 }}>(optionnel)</span></label>
                  <textarea rows={2} placeholder="Remarque interne pour la comptabilite..." value={form.notes_compta} onChange={e => setForm(f => ({ ...f, notes_compta: e.target.value }))} style={{ ...INPUT_ST, marginTop: 4, resize: 'vertical' }} />
                </div>
                <div style={{ background: '#F0F5F0', border: '1px solid #D9E6DA', borderRadius: 10, padding: '12px 14px', fontSize: 12.5, display: 'flex', gap: 8, color: C.green2, marginBottom: 14 }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}><circle cx="12" cy="12" r="10"/><path d="M9 12l2 2 4-4"/></svg>
                  <span>Apres confirmation, le pret devient <strong>actif immediatement</strong>. La dette est creee dans le systeme et les notifications sont envoyees.</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, paddingTop: 14, borderTop: `1px solid ${C.line2}`, flexWrap: 'wrap' }}>
                  <button onClick={onBack} style={BTN_G}>Annuler</button>
                  <button disabled={loading || !form.reference_virement} onClick={debloquer} style={BTN_P}>
                    {loading ? 'Confirmation...' : 'Confirmer — Activer le pret'} <IcoCheck />
                  </button>
                </div>
              </>
            ) : (
              <div style={{ color: C.ink3, fontSize: 13, textAlign: 'center', padding: 16 }}>
                Statut : <BadgeStatut statut={demande.statut} />
                {demande.reference_virement && <CheckRow k="Reference" v={demande.reference_virement} noTag />}
                {demande.date_deblocage && <CheckRow k="Date deblocage" v={fmtDate(demande.date_deblocage)} noTag />}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── VueGlobale ───────────────────────────────────────────────────────────────
function VueGlobale({ token, perms, role }) {
  const [demandes, setDemandes] = useState([]);
  const [selected, setSelected] = useState(null);
  const [filtre, setFiltre]     = useState('');
  const [stats, setStats]       = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  const charger = useCallback(() => {
    const q = filtre ? `?statut=${filtre}` : '';
    axios.get(`${API}/api/prets-sogas/demandes${q}`, { headers }).then(r => setDemandes(toArray(r.data.demandes || r.data))).catch(() => {});
    axios.get(`${API}/api/prets-sogas/dashboard`, { headers }).then(r => setStats(r.data.stats)).catch(() => {});
  }, [token, filtre]);

  useEffect(() => { charger(); }, [charger]);

  if (selected) return <DetailDemande id={selected} token={token} onBack={() => { setSelected(null); charger(); }} />;

  const FILTRES = [
    { k: '', label: 'Tous' }, { k: 'soumis', label: 'En attente de décision' },
    { k: 'approuve_dg', label: 'Approuvé DG' }, { k: 'traite_rh', label: 'En cours RH' },
    { k: 'transmis_compta', label: 'Transmis à la compta' },
    { k: 'actif', label: 'En cours' }, { k: 'solde', label: 'Soldé' }, { k: 'refuse', label: 'Refusé' },
  ];

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Prêts SOGAS — Vue globale</h2>
        <p style={{ margin: '4px 0 0', color: C.ink3, fontSize: 14 }}>Gestion et suivi de toutes les demandes</p>
      </div>

      {stats && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 12 }}>
            {[
              { label: 'En attente DG',    val: stats.en_attente_dg     || 0, color: C.orange },
              { label: 'En attente RH',    val: stats.en_attente_rh     || 0, color: C.indigo },
              { label: 'Att. Compta',      val: stats.en_attente_compta || 0, color: C.purple },
              { label: 'Actifs',           val: stats.actifs            || 0, color: C.green },
            ].map(s => (
              <div key={s.label} style={{ background: '#fff', border: `0.5px solid ${C.line}`, borderRadius: R, padding: '14px 18px', boxShadow: SHADOW }}>
                <div style={{ fontSize: 26, fontWeight: 800, color: s.color, fontVariantNumeric: 'tabular-nums' }}>{s.val}</div>
                <div style={{ fontSize: 11.5, color: C.ink3, marginTop: 2 }}>{s.label}</div>
              </div>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 20 }}>
            <div style={{ background: C.greenSoft, borderRadius: R, padding: '14px 18px' }}>
              <div style={{ fontSize: 11.5, color: C.green, marginBottom: 4 }}>Encours total</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: C.green, fontVariantNumeric: 'tabular-nums' }}>{fmt(stats.encours_total)}</div>
            </div>
            <div style={{ background: C.blueSoft, borderRadius: R, padding: '14px 18px' }}>
              <div style={{ fontSize: 11.5, color: C.blue, marginBottom: 4 }}>Mensualités totales</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: C.blue, fontVariantNumeric: 'tabular-nums' }}>{fmt(stats.mensualites_total)}/mois</div>
            </div>
          </div>
        </>
      )}

      <div style={{ display: 'inline-flex', background: '#fff', border: `1px solid ${C.line}`, borderRadius: 10, padding: 3, gap: 2, marginBottom: 20, overflowX: 'auto', maxWidth: '100%' }}>
        {FILTRES.map(f => (
          <button key={f.k} onClick={() => setFiltre(f.k)} style={{ border: 0, background: filtre === f.k ? C.green : 'transparent', color: filtre === f.k ? '#fff' : C.ink2, padding: '7px 11px', borderRadius: 7, cursor: 'pointer', fontWeight: 500, fontSize: 13, whiteSpace: 'nowrap', flexShrink: 0, fontFamily: FONT }}>
            {f.label}
          </button>
        ))}
      </div>

      {demandes.length === 0 ? (
        <div style={{ textAlign: 'center', color: C.ink3, padding: 60, fontSize: 14 }}>Aucune demande</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {demandes.map(d => (
            <div key={d.id} style={{ background: '#fff', border: `0.5px solid ${C.line}`, borderRadius: R, padding: 18, boxShadow: SHADOW, marginBottom: 12, cursor: 'pointer' }} onClick={() => setSelected(d.id)}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 14, flexWrap: 'wrap', marginBottom: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <AvatarCircle prenom={d.prenom} nom={d.nom} size={42} />
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: C.ink }}>{d.prenom} {d.nom}</div>
                    <div style={{ fontSize: 12.5, color: C.ink3, marginTop: 2, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <span>{d.matricule}</span>
                      {d.numero_dossier && <><span>·</span><span>{d.numero_dossier}</span></>}
                    </div>
                  </div>
                </div>
                <BadgeStatut statut={d.statut} />
              </div>
              <KVGrid cols={4} items={[
                { k: 'Montant demandé', v: fmt(d.montant_demande) },
                { k: 'Durée',           v: `${d.duree_mois} mois` },
                { k: 'Mensualité',      v: fmt(d.mensualite_retenue || Math.round(d.montant_demande / d.duree_mois)) },
                { k: 'Soumis le',       v: fmtDate(d.created_at) },
              ]} />
              <LigneSuivi suivi={d.suivi} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Page principale ──────────────────────────────────────────────────────────
export default function PretsSogasPage() {
  const { employe, token } = useAuth();
  const [permsVivantes, setPermsVivantes] = useState(employe?.permissions_effectives || {});
  const [seuil, setSeuil] = useState(50);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/api/auth/me`, { headers })
      .then(r => { if (r.data.employe?.permissions_effectives) setPermsVivantes(r.data.employe.permissions_effectives); })
      .catch(() => {});
    axios.get(`${API}/api/prets-sogas/parametres`, { headers })
      .then(r => { const p = (r.data || []).find(x => x.cle === 'seuil_solvabilite_pct'); if (p) setSeuil(parseFloat(p.valeur) || 50); })
      .catch(() => {});
  }, [employe?.id]);

  const role      = employe?.role || '';
  const estRH     = ROLES_RH.includes(role);
  const isAdmin   = role === 'admin';

  // Permissions basées sur les toggles du poste — pas de bypass estRH pour les onglets spécialisés
  const peutSoumettre  = isAdmin || estRH || permsVivantes?.peut_soumettre_pret_sogas  === true;
  const peutApprouver  = isAdmin || ROLES_DG.includes(role) || permsVivantes?.peut_approuver_pret_sogas  === true;
  const peutTraiter    = isAdmin || permsVivantes?.peut_traiter_pret_sogas    === true;
  const peutDebloquer  = isAdmin || permsVivantes?.peut_debloquer_pret_sogas  === true;
  const peutVoirTout   = isAdmin || estRH || permsVivantes?.peut_voir_tous_prets_sogas === true;

  const aucunAcces = !peutSoumettre && !peutApprouver && !peutTraiter && !peutDebloquer && !peutVoirTout;

  // Onglet par defaut selon permissions effectives
  const defaultOnglet = () => {
    if (peutApprouver && ROLES_DG.includes(role)) return 'dg';
    if (peutTraiter) return 'rh';
    if (peutDebloquer) return 'compta';
    if (peutVoirTout) return 'global';
    if (peutSoumettre) return 'mes-demandes';
    return 'no-access';
  };
  const [onglet, setOnglet] = useState(defaultOnglet);
  const [selectedDG, setSelectedDG]     = useState(null);
  const [selectedRH, setSelectedRH]     = useState(null);
  const [selectedCompta, setSelectedCompta] = useState(null);

  const tabStyle = (actif) => ({
    border: 0, background: actif ? C.green : 'transparent', color: actif ? '#fff' : C.ink2,
    padding: '7px 14px', borderRadius: 7, cursor: 'pointer',
    fontWeight: 500, fontSize: 13, whiteSpace: 'nowrap', fontFamily: FONT,
  });

  const renderContent = () => {
    if (aucunAcces) return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '64px 24px', textAlign: 'center' }}>
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={C.ink4} strokeWidth="1.5" strokeLinecap="round" style={{ marginBottom: 16 }}>
          <circle cx="12" cy="12" r="10"/><path d="M4.93 4.93l14.14 14.14"/>
        </svg>
        <div style={{ fontSize: 15, fontWeight: 600, color: C.ink2, marginBottom: 6 }}>Vous n'avez pas acces a ce module</div>
        <div style={{ fontSize: 13, color: C.ink4 }}>Contactez votre administrateur pour obtenir les permissions necessaires.</div>
      </div>
    );
    if (onglet === 'mes-demandes') return <VueEmploye employe={employe} token={token} />;
    if (onglet === 'dg') {
      if (selectedDG) return <VueDGDetail id={selectedDG} token={token} seuil={seuil} onBack={() => setSelectedDG(null)} />;
      return <VueDGListe token={token} seuil={seuil} onVoirDetail={(id) => setSelectedDG(id)} onAction={() => {}} />;
    }
    if (onglet === 'rh') {
      if (selectedRH) return <VueRHDetail id={selectedRH} token={token} seuil={seuil} onBack={() => setSelectedRH(null)} />;
      return <VueRHListe token={token} seuil={seuil} onVoirDetail={(id) => setSelectedRH(id)} />;
    }
    if (onglet === 'compta') {
      if (selectedCompta) return <VueComptaDetail id={selectedCompta} token={token} onBack={() => setSelectedCompta(null)} />;
      return <VueComptaListe token={token} seuil={seuil} onVoirDetail={(id) => setSelectedCompta(id)} />;
    }
    if (onglet === 'global') return <VueGlobale token={token} perms={permsVivantes} role={employe?.role} />;
    return null;
  };

  return (
    <Layout>
      <style>{`
        .sogas-amort-wrap { overflow-x: auto; }
        .sogas-amort-table { width: 100%; border-collapse: collapse; }
        .sogas-amort-cards { display: none; }
        .sogas-pants-wrap { overflow-x: auto; }
        .sogas-pants-table { width: 100%; border-collapse: collapse; }
        .sogas-pants-cards { display: none; }
        .sogas-histo-wrap { overflow-x: auto; }
        .sogas-histo-table { width: 100%; border-collapse: collapse; }
        .sogas-histo-cards { display: none; }
        @media (max-width: 767px) {
          .sogas-amort-wrap { overflow-x: visible; }
          .sogas-amort-table { display: none; }
          .sogas-amort-cards { display: block; }
          .sogas-pants-wrap { overflow-x: visible; }
          .sogas-pants-table { display: none; }
          .sogas-pants-cards { display: block; }
          .sogas-histo-wrap { overflow-x: visible; }
          .sogas-histo-table { display: none; }
          .sogas-histo-cards { display: block; }
        }
      `}</style>
      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '24px 20px', fontFamily: FONT, background: C.bg, minHeight: '100vh' }}>
        {/* Segmented control tabs — masques selon permissions poste */}
        {!aucunAcces && (
          <div style={{ display: 'inline-flex', background: '#fff', border: `1px solid ${C.line}`, borderRadius: 10, padding: 3, marginBottom: 24, gap: 2, overflowX: 'auto', maxWidth: '100%' }}>
            {peutSoumettre && (
              <button onClick={() => setOnglet('mes-demandes')} style={tabStyle(onglet === 'mes-demandes')}>Mes demandes</button>
            )}
            {peutApprouver && (
              <button onClick={() => { setOnglet('dg'); setSelectedDG(null); }} style={tabStyle(onglet === 'dg')}>Validation DG</button>
            )}
            {peutTraiter && (
              <button onClick={() => { setOnglet('rh'); setSelectedRH(null); }} style={tabStyle(onglet === 'rh')}>Traitement RH</button>
            )}
            {peutDebloquer && (
              <button onClick={() => { setOnglet('compta'); setSelectedCompta(null); }} style={tabStyle(onglet === 'compta')}>Déblocage Compta</button>
            )}
            {peutVoirTout && (
              <button onClick={() => { setOnglet('global'); }} style={tabStyle(onglet === 'global')}>Toutes les demandes</button>
            )}
          </div>
        )}

        {renderContent()}
      </div>
    </Layout>
  );
}
